// src/components/reservation/ReservationFlow.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createAuditedClient } from '../../utils/auditClient';
import { getUrl } from 'aws-amplify/storage';
import { PHONE_COUNTRY_CODES } from '../../constants/countryDialCodes';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  ArrowRight,
  CalendarDays,
  CarFront,
  CheckCircle2,
  Clock3,
  CreditCard,
  Loader2,
  LockKeyhole,
  Mail,
  MapPin,
  ShieldCheck,
} from "lucide-react";
import { fromZonedTime } from "date-fns-tz";

import {
  // core helpers + constants
  safeStr,
  digitsOnly,
  locationCodeFromCountry,
  pickBestCompany,
  timeZoneForCountryCode,
  nowLocalInputInZone,
  addHoursLocalInputInZone,
  toISOInZone,
  parseLocalDT,
  formatDateTimeNice,
  normalizeExtras,
  normalizeCrossBorderCountries,
  crossBorderFeeUsdForExtras,
  quoteKey,
  insurancePerDayForReservation,
  addonsMoneyCurrencyFor,
  normalizeBreakdown,
  getSiteDiscountSummary,
  getBackendAddonsTotalFromBreakdown,
  getCrossBorderAddonsAdjustment,
  applyCrossBorderCountryPricingToBreakdown,
  calcClientAddonsTotal,
  calcDepositAmount,
  calcPolicyDeposit,
  computeDeliveryFee,
  groupCheapestTotal,
  toUSD,
  convertCurrency,
  normalizeFxRates,
  formatMoney,
  BACKEND_CURRENCY,
  DISPLAY_CURRENCY_OPTIONS,
  FX_BASE,
  MIN_RENTAL_DAYS,
  GEO_DELIVERY_FEE_PER_LEG,
  AL_DELIVERY_FEE_PER_LEG,
  TBILISI_AREAS,
  TIRANA_AREAS,
  BASE_EXTRAS,
  CROSS_BORDER_ALLOWED_COUNTRIES,
  CROSS_BORDER_FEE_USD,
  overlaps,
  calcRentalDays,
  roundMoney,
  SITE_RESERVATION_DISCOUNT_LABEL,
  MAX_SEAT_EXTRA_COUNT,
  carModelYear,
  formatCarNameWithoutYear,
  formatCarNameWithoutYearPlate,
  normalizeSeatExtraCount,
} from "./reservationFlow.logic";

import {
  SelectPopover,
  FancyDateRangePicker,
  LocationModeToggle,
  Stepper,
  Notice,
  SpecChip,
  Divider,
  GroupAvailabilityCard,
  // styles
  grid2,
  label,
  inputStyle,
  alertBad,
  ctaPrimary,
  ctaSecondary,
  extrasGrid,
  extraRow,
  extraNote,
  qtyBtn,
  reviewCard,
  reviewGrid,
  reviewItem,
  reviewLabel,
  reviewValue,
  priceRow,
  priceLeft,
  priceRight,
} from "./reservationFlow.ui";
import "./ReservationFlow.css";

/* -------------------- auth mode -------------------- */
async function smartAuthMode() {
  const s = await fetchAuthSession();
  if (s?.tokens?.accessToken) return 'userPool';
  if (s?.credentials) return 'iam';
  return 'iam';
}

const client = createAuditedClient('ReservationFlow');
async function paymentsFetch(path, body) {
  const base = getPaymentsApiBase();
  if (!base) throw new Error("Payments API base missing (REACT_APP_PAYMENTS_API_BASE).");

  const url = `${base}${path}`;

  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body || {}),
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = data?.message || data?.error || `Payments API error (${res.status})`;
    const explanation = safeStr(data?.details?.error?.explanation);
    const supportCode = safeStr(data?.details?.error?.supportCode);
    const context = [
      explanation && explanation !== msg ? explanation : "",
      supportCode ? `Support code: ${supportCode}` : "",
    ].filter(Boolean);
    throw new Error(context.length ? `${msg}. ${context.join(" ")}` : msg);
  }
  return data;
}

/* -------------------- helpers -------------------- */

function didRedirectFor(ref) {
  try { return sessionStorage.getItem(`pay_redirect_${ref}`) === '1'; } catch { return false; }
}
function markRedirectFor(ref) {
  try { sessionStorage.setItem(`pay_redirect_${ref}`, '1'); } catch { /* ignore */ }
}

/* -------------------- payments -------------------- */

// CRA/Webpack build-time env only (browser cannot read real env at runtime)
const PAYMENTS_API_BASE =
  safeStr(process.env.REACT_APP_PAYMENTS_API_BASE);

function getPaymentsApiBase() {
  return PAYMENTS_API_BASE;
}

function paymentProviderFor(phoneCountryCode) {
  return safeStr(phoneCountryCode) === '+972' ? 'MANUAL' : 'MPGS';
}



// For fees/deposits that are local policy amounts (not from backend quotes)
function localCurrencyForCountryCode(countryCode) {
  const c = safeStr(countryCode).toUpperCase();
  if (c === 'AL') return 'EUR';
  if (c === 'GE') return 'USD';
  return 'USD';
}


// Single payments caller (re-use the auth-aware one above)
async function paymentsPost(path, body) {
  return paymentsFetch(path, body);
}


function buildReturnUrl(inboundRequestId) {
  const u = new URL("/payment/return", window.location.origin);
  u.searchParams.set("iid", String(inboundRequestId));
  return u.toString();
}


const MIN_START_LEAD_HOURS = 12;
const BRAND_GREEN_DARK = '#67c945';
const BRAND_GREEN_TEXT = '#163510';
const BRAND_GREEN_SOFT = '#effbe9';
const BRAND_GREEN_SOFT_BORDER = '#bde9ab';
const BRAND_GREEN_SHADOW = 'rgba(126,217,87,0.30)';
const RESERVATION_STEP_META = [
  {
    title: 'Plan your trip',
    description: 'Choose your dates and the most convenient pick-up and return points.',
  },
  {
    title: 'Choose your car',
    description: 'Compare the vehicles available for your exact trip.',
  },
  {
    title: 'Tailor your rental',
    description: 'Add coverage and extras with clear, upfront pricing.',
  },
  {
    title: 'Your details',
    description: 'Tell us who is driving and how we can reach you.',
  },
  {
    title: 'Review and book',
    description: 'Check every detail before sending your request or paying securely.',
  },
];
const ONLINE_RESERVATION_TERMS_TITLE = 'HASTE - Online Reservation Terms & Conditions';
const ONLINE_RESERVATION_TERMS_VERSION = '2026-04-15';
const ONLINE_RESERVATION_TERMS_LAST_UPDATED = 'April 15, 2026';
const ONLINE_RESERVATION_TERMS_CHECKBOX_LABEL =
  'I have read and accept the Online Reservation Terms & Conditions.';
const ONLINE_RESERVATION_TERMS_SECTIONS = [
  {
    title: '1. Definitions and Scope',
    items: [
      'These Online Reservation Terms & Conditions ("Terms") govern vehicle reservation requests made through the Haste website or any affiliated booking platform (the "Platform").',
      'The person submitting the reservation request is referred to in these Terms as the "Customer".',
      'These Terms govern the online reservation process only. The actual vehicle handover and rental remain subject to the operational handover process and any rental documentation presented at pickup.',
    ],
  },
  {
    title: '2. Reservation Nature and Confirmation',
    items: [
      'A reservation submitted through the Platform is a request to reserve a vehicle for the dates, location, and vehicle information selected during booking.',
      'A reservation is not instantly confirmed.',
      'A reservation becomes confirmed only after Haste approves it, or after payment is verified when payment is required.',
    ],
  },
  {
    title: '3. Payment, Authorization and Pricing',
    items: [
      'By proceeding with payment, the Customer authorizes Haste to process the payment method provided for the amount displayed in the reservation flow.',
      'Depending on the booking shown on the Platform, online payment may represent all or part of the rental amount.',
      'Any security deposit is separate from the online payment unless expressly stated otherwise in the reservation flow.',
      'The Customer confirms that they are the authorized holder of the payment method used.',
    ],
  },
  {
    title: '4. Vehicle Availability and Operational Changes',
    items: [
      'All reservation requests are subject to vehicle availability and operational feasibility.',
      'If the reserved vehicle cannot be provided due to technical, operational, or availability reasons, Haste may contact the Customer to modify, replace, reschedule, or cancel the reservation.',
      'Changes requested by the Customer remain subject to availability and Haste approval.',
    ],
  },
  {
    title: '5. Customer Eligibility and Required Documents',
    items: [
      'The minimum rental age is 21.',
      'The main driver must hold a valid driving license that meets the rental requirements.',
      'At handover, the main driver must present original physical documents: a valid passport or national ID, and the original physical driving license.',
      'Digital copies of required documents are not accepted.',
      'Failure to satisfy the document or eligibility requirements may prevent vehicle handover and completion of the rental.',
    ],
  },
  {
    title: '6. Arrival Changes and Flight Details',
    items: [
      'Customers should provide their flight number when booking if arrival coordination is needed.',
      'If a flight or arrival time changes, the Customer must contact Haste as soon as possible.',
      'Haste will do its best to adjust the pickup time based on the updated arrival details and vehicle availability.',
    ],
  },
  {
    title: '7. Cancellation and Modification',
    items: [
      'Cancellation requests made less than 72 hours before the reservation start date are non-refundable.',
      'Cancellation or modification requests do not become effective until acknowledged by Haste.',
    ],
  },
  {
    title: '8. Security Deposit',
    items: [
      'A security deposit may be required depending on the vehicle category and the rental country.',
      'Applicable deposit terms are shown during the reservation flow.',
      'In many cases, the deposit is held on a card until the vehicle is returned and checked.',
    ],
  },
  {
    title: '9. Cross-Border Use',
    items: [
      'Cross-border driving is not automatic and must be approved in advance.',
      'Cross-border availability depends on the rental country and vehicle.',
    ],
  },
  {
    title: '10. Mileage',
    items: ['Standard rentals include unlimited mileage.'],
  },
  {
    title: '11. Insurance, Damage and Incident Reporting',
    items: [
      'Standard rentals include third-party liability insurance.',
      'Full coverage may be added where available.',
      'Insurance coverage applies only if the Customer follows the rental terms and reports any accident or damage immediately.',
      'Any accident, damage, breakdown, or technical issue during the rental must be reported to Haste immediately.',
    ],
  },
  {
    title: '12. Roadside Assistance',
    items: [
      'Haste provides roadside assistance for breakdowns, accidents, or technical issues reported during the rental.',
      'The Customer must follow Haste instructions and cooperate with the required next steps.',
    ],
  },
  {
    title: '13. Communications and Acceptance',
    items: [
      'The Customer agrees that Haste may contact them by phone, email, or messaging applications for reservation and operational coordination purposes.',
      'By completing the reservation or payment process, the Customer confirms that they have read and accepted these Terms.',
    ],
  },
];


function normalizeGroupKey(name) {
  return safeStr(name).toLowerCase().replace(/\s+/g, ' ');
}

/* -------------------- GraphQL -------------------- */
const RES_BY_CAR_BY_START = /* GraphQL */ `
  query ReservationsByCarByStart($carId: ID!, $start: ModelStringKeyConditionInput, $limit: Int) {
    reservationsByCarByStart(carId: $carId, start: $start, limit: $limit) {
      items { id start end status approvalStatus }
    }
  }
`;

const MAINT_BY_CAR_BY_START = /* GraphQL */ `
  query MaintenanceByCarByStart($carId: ID!, $start: ModelStringKeyConditionInput, $limit: Int) {
    maintenanceByCarByStart(carId: $carId, start: $start, limit: $limit) {
      items { id start end approvalStatus }
    }
  }
`;

const SUBMIT_INBOUND_REQUEST = /* GraphQL */ `
  mutation SubmitInboundRequest($input: SubmitInboundRequestInput!) {
    submitInboundRequest(input: $input) {
      id
      status
    }
  }
`;

const QUOTE_PUBLIC_RESERVATION = /* GraphQL */ `
  query QuotePublicReservation($input: QuotePublicReservationInput!) {
    quotePublicReservation(input: $input) {
      currency
      days
      breakdownJson
      total
      payableNow
      quoteId
    }
  }
`;

const ADDONS_BY_COMPANY_BY_COUNTRY = /* GraphQL */ `
  query AddonsByCompanyByCountry($CompanyId: String!, $countryCode: ModelStringKeyConditionInput) {
    addonsByCompanyByCountry(CompanyId: $CompanyId, countryCode: $countryCode, limit: 10) {
      items {
        id
        CompanyId
        countryCode
        currency

        insuranceTiers
        insurancePerDay_1_5
        insurancePerDay_6_plus

        boosterSeatPerDay
        infantSeatPerDay
        babySeatPerDay
        snowChainsPerDay
        extraDriverPerDay
        updatedAt
        createdAt
      }
    }
  }
`;

/* -------------------- Main component -------------------- */
export default function ReservationFlow({
  companies,
  cars,
  dataLoading,
  companyCountryOptions = ['Georgia'],
  turnstileSiteKey = '',
  isMobile = false,
}) {

  const defaultCountryCode = locationCodeFromCountry('Georgia');
  const defaultTz = timeZoneForCountryCode(defaultCountryCode);
  const [payErr, setPayErr] = useState(null);
  const [payStatus, setPayStatus] = useState("idle"); // idle|creating|redirecting|verifying|paid|failed
  const [payReceipt] = useState(null); // verify response summary

  async function startMpgsHostedCheckout({ inboundRequestId, amount, currency }) {
    setPayErr(null);

    if (!getPaymentsApiBase()) throw new Error("Payments API base missing ( REACT_APP_PAYMENTS_API_BASE).");
    if (!inboundRequestId) throw new Error("Missing inboundRequestId.");

    setPayStatus("creating");

    const returnUrl = buildReturnUrl(inboundRequestId);

    const resp = await paymentsPost("/prod/mpgs/create-session-rental", {
      inboundRequestId,
      // send amount as a hint only; backend must compute/validate
      amount: Number(amount),
      currency: String(currency || "USD").toUpperCase(),
      customerEmail: safeStr(email),
      customerPhone: safeStr(phonePrimary),
      returnUrl,
      description: `Haste payment (${inboundRequestId})`,
    });

    const sessionId = safeStr(resp?.sessionId);
    const payUrl = safeStr(resp?.payUrl);

    const payload = {
      inboundRequestId: String(inboundRequestId),
      orderId: safeStr(resp?.orderId),
      sessionId,
      nonce: safeStr(resp?.nonce),
      successIndicator: safeStr(resp?.successIndicator),
    };
    if (!payload.orderId) {
      setPayStatus("failed");
      throw new Error("Missing orderId from create-session (cannot verify later).");
    }
    if (!payUrl) {
      setPayStatus("failed");
      throw new Error(`No Checkout SDK launch URL returned from create-session. Got: ${JSON.stringify(resp)}`);
    }

    if (!payload.successIndicator) {
      setPayStatus("failed");
      throw new Error("Missing successIndicator from create-session.");
    }

    try {
      sessionStorage.setItem(`mpgs_${inboundRequestId}`, JSON.stringify(payload));
    } catch {
      // ignore
    }

    setPayStatus("redirecting");
    window.location.assign(payUrl);
  }

  useEffect(() => {
    function redirectLegacyReturn() {
      const url = new URL(window.location.href);
      if (url.searchParams.get("payReturn") !== "1") return;
      const redirectUrl = new URL("/payment/return", window.location.origin);
      for (const key of ["iid", "rid", "nonce", "resultIndicator"]) {
        const value = safeStr(url.searchParams.get(key));
        if (value) redirectUrl.searchParams.set(key, value);
      }
      window.location.replace(redirectUrl.toString());
    }

    redirectLegacyReturn();
  }, []);

  const [step, setStep] = useState(1);
  const [redirectingPay, setRedirectingPay] = useState(false);

  const [country, setCountry] = useState('');
  const [startLocal, setStartLocal] = useState(() => {
    const base = nowLocalInputInZone(defaultTz); // "YYYY-MM-DDTHH:mm" in tz
    const [dPart, tPart] = base.split("T");
    const hh = (tPart || "00:00").slice(0, 2);
    const rounded = `${dPart}T${hh}:00`;
    return addHoursLocalInputInZone(rounded, MIN_START_LEAD_HOURS, defaultTz);
  });

  const [endLocal, setEndLocal] = useState(() => {
    // start + 4 days at 10:00 in tz
    const baseStart = (() => {
      const base = nowLocalInputInZone(defaultTz);
      const [dPart, tPart] = base.split("T");
      const hh = (tPart || "00:00").slice(0, 2);
      const rounded = `${dPart}T${hh}:00`;
      return addHoursLocalInputInZone(rounded, MIN_START_LEAD_HOURS, defaultTz);
    })();

    const plus4Days = addHoursLocalInputInZone(baseStart, 24 * 4, defaultTz);
    const { date } = parseLocalDT(plus4Days);
    return `${date}T10:00`;
  });


  const [pickupMode, setPickupMode] = useState('FREE');   // FREE | DELIVERY
  const [dropoffMode, setDropoffMode] = useState('FREE'); // FREE | DELIVERY

  // FREE mode
  const [pickupFree, setPickupFree] = useState('');
  const [dropoffFree, setDropoffFree] = useState('');

  // DELIVERY mode (search + selected area + optional note)
  const [pickupArea, setPickupArea] = useState('');
  const [dropoffArea, setDropoffArea] = useState('');
  const [pickupExactNote, setPickupExactNote] = useState('');
  const [dropoffExactNote, setDropoffExactNote] = useState('');


  const [selectedCar, setSelectedCar] = useState(null);
  const [openGroupKey, setOpenGroupKey] = useState(null);
  const [payerCountryCode, setPayerCountryCode] = useState('');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phonePrimaryDigits, setPhonePrimaryDigits] = useState('');
  const [phoneWhatsapp, setPhoneWhatsapp] = useState(''); // keep as-is (optional)
  const isManualPhone = useMemo(() => safeStr(payerCountryCode) === '+972', [payerCountryCode]);

  const phonePrimary = useMemo(() => {
    const code = safeStr(payerCountryCode);      // like "+961"
    const num = digitsOnly(phonePrimaryDigits);  // local digits only
    if (!code || !num) return '';
    return `${code}${num}`; // forced code, single combined value
  }, [payerCountryCode, phonePrimaryDigits]);

  const [flightNumber, setFlightNumber] = useState('');
  const [custMessage, setCustMessage] = useState('');

  const [extras, setExtras] = useState({ ...BASE_EXTRAS });
  const [insuranceTier, setInsuranceTier] = useState('THIRD_PARTY');

  const [searching, setSearching] = useState(false);
  const [availabilityErr, setAvailabilityErr] = useState(null);
  const [availableCars, setAvailableCars] = useState([]);

  const [carImageUrls, setCarImageUrls] = useState({});

  const [quotesByKey, setQuotesByKey] = useState({});

  const selectedQuoteKey = useMemo(() => {
    if (!selectedCar?.id) return null;
    return quoteKey(selectedCar.id, insuranceTier, extras);
  }, [selectedCar?.id, insuranceTier, extras]);

  const selectedQuoteEntry = selectedQuoteKey ? quotesByKey[selectedQuoteKey] : null;

  const quote = selectedQuoteEntry?.quote || null;
  const quoteLoading = !!selectedQuoteEntry?.loading;
  const quoteErr = selectedQuoteEntry?.err || null;

  const [addonsPricing, setAddonsPricing] = useState(null);
  const [addonsLoading, setAddonsLoading] = useState(false);
  const [addonsErr, setAddonsErr] = useState(null);

  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitErr, setSubmitErr] = useState(null);
  const [submitOk, setSubmitOk] = useState(null);
  // --- anti-spam / turnstile ---
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileReady, setTurnstileReady] = useState(false);
  const [turnstileError, setTurnstileError] = useState(null);
  const turnstileElRef = useRef(null);
  const turnstileWidRef = useRef(null);

  const [hpField, setHpField] = useState('');
  const [formTs] = useState(() => Date.now());
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsExpanded, setTermsExpanded] = useState(false);
  const hasSearchedRef = useRef(false);
  // locks step 2+ until user clicks "Check availability" again
  const [, setNeedsRecheck] = useState(false);

  // skip invalidation on first render for each field watcher
  const didMountTripRef = useRef(false);

  function resetTripFieldsForCountry(nextCountryLabel) {
    const nextCC = locationCodeFromCountry(nextCountryLabel);
    const nextTz = timeZoneForCountryCode(nextCC);

    const base = nowLocalInputInZone(nextTz);
    const [dPart, tPart] = base.split("T");
    const hh = (tPart || "00:00").slice(0, 2);
    const rounded = `${dPart}T${hh}:00`;

    const nextStart = addHoursLocalInputInZone(rounded, MIN_START_LEAD_HOURS, nextTz);

    const plus4Days = addHoursLocalInputInZone(nextStart, 24 * 4, nextTz);
    const { date } = parseLocalDT(plus4Days);
    const nextEnd = `${date}T10:00`;

    setStartLocal(nextStart);
    setEndLocal(nextEnd);

    setPickupMode('FREE');
    setDropoffMode('FREE');

    setPickupFree('');
    setDropoffFree('');

    setPickupArea('');
    setDropoffArea('');

    setPickupExactNote('');
    setDropoffExactNote('');

    clearAvailabilityUI({ keepStep: false });

    hasSearchedRef.current = false;
    setNeedsRecheck(false);
  }


  function invalidateAfterTripChange() {
    // only matters if user already searched once
    if (!hasSearchedRef.current) return;

    // reset step 2+ and force user to re-check
    clearAvailabilityUI({ keepStep: false });
    hasSearchedRef.current = false; // prevent any “post-search” behaviors
    setNeedsRecheck(true);
  }
  const reservationTopRef = useRef(null);
  const previousStepRef = useRef(step);
  const [displayCurrency, setDisplayCurrency] = useState(() => {
    // default: local currency per country
    const cc = locationCodeFromCountry(country);
    return localCurrencyForCountryCode(cc);
  });

  const [fxRates, setFxRates] = useState(() => ({ USD: 1 }));
  const [fxLoading, setFxLoading] = useState(false);
  const [fxErr, setFxErr] = useState(null);
  const [, setFxMeta] = useState(null);
  useEffect(() => {
    const cc = locationCodeFromCountry(country);
    setDisplayCurrency(localCurrencyForCountryCode(cc));
  }, [country]);

  async function fetchLatestFx() {
    setFxLoading(true);
    setFxErr(null);
    try {
      // Open access endpoint (no key). Updates daily.
      const res = await fetch(`https://open.er-api.com/v6/latest/${FX_BASE}`);
      if (!res.ok) throw new Error(`FX fetch failed (${res.status})`);
      const data = await res.json();

      if (data?.result !== 'success') throw new Error('FX provider returned error.');
      const norm = normalizeFxRates(data?.rates);

      setFxRates(norm);
      setFxMeta({
        base: data?.base_code,
        lastUpdateUtc: data?.time_last_update_utc,
        nextUpdateUtc: data?.time_next_update_utc,
        provider: data?.provider,
        documentation: data?.documentation,
      });
    } catch (e) {
      setFxErr(e?.message || 'Failed to load exchange rates.');
    } finally {
      setFxLoading(false);
    }
  }

  useEffect(() => {
    fetchLatestFx();

    // optional: refresh when tab gains focus (don’t spam; provider is rate-limited)
    const onFocus = () => fetchLatestFx();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fmt = useMemo(() => {
    const dc = safeStr(displayCurrency).toUpperCase() || 'USD';

    // fmt(amount, sourceCurrency) -> formatted in display currency
    return (amount, sourceCurrency = 'USD') => {
      const from = safeStr(sourceCurrency).toUpperCase() || 'USD';
      const converted = convertCurrency(amount, from, dc, fxRates);
      if (converted == null) {
        // Fail-soft: show source value if conversion missing
        return formatMoney(amount, from);
      }
      return formatMoney(converted, dc);
    };
  }, [displayCurrency, fxRates]);


  const getScrollParent = useCallback((el) => {
    if (!el) return null;
    let p = el.parentElement;
    while (p && p !== document.body && p !== document.documentElement) {
      const s = window.getComputedStyle(p);
      const oy = s.overflowY;
      const canScrollVertically = (oy === 'auto' || oy === 'scroll' || oy === 'overlay')
        && p.scrollHeight > p.clientHeight + 1;
      if (canScrollVertically) return p;
      p = p.parentElement;
    }
    return null;
  }, []);

  const getStepScrollOffsets = useCallback(() => {
    const headerEl = document.querySelector('.lp-header');
    const headerHeight = headerEl?.getBoundingClientRect?.().height || 0;

    return {
      page: Math.max(headerHeight + 16, isMobile ? 88 : 56),
      container: 12,
    };
  }, [isMobile]);

  const scrollToReservationTop = useCallback(() => {
    const el = reservationTopRef.current;
    if (!el) return;

    const { page: pageOffset, container: containerOffset } = getStepScrollOffsets();

    // wait for the new step to render and account for the fixed landing-page header
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const scrollParent = getScrollParent(el);
        const behavior = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ? 'auto' : 'smooth';

        if (scrollParent) {
          const parentRect = scrollParent.getBoundingClientRect();
          const elRect = el.getBoundingClientRect();
          const top = (elRect.top - parentRect.top) + scrollParent.scrollTop - containerOffset;
          scrollParent.scrollTo({ top: Math.max(0, top), behavior });
        } else {
          const top = el.getBoundingClientRect().top + window.scrollY - pageOffset;
          window.scrollTo({ top: Math.max(0, top), behavior });
        }
      });
    });
  }, [getScrollParent, getStepScrollOffsets]);

  const goToStep = useCallback((nextStep) => {
    if (step === nextStep) {
      scrollToReservationTop();
      return;
    }
    setStep(nextStep);
  }, [step, scrollToReservationTop]);

  useEffect(() => {
    if (previousStepRef.current === step) return;
    previousStepRef.current = step;
    scrollToReservationTop();
  }, [step, scrollToReservationTop]);

  useEffect(() => {
    if (!turnstileSiteKey) return;

    // inject script once
    if (!document.querySelector('script[data-turnstile="1"]')) {
      const s = document.createElement('script');
      s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      s.async = true;
      s.defer = true;
      s.dataset.turnstile = '1';
      s.onload = () => setTurnstileReady(true);
      s.onerror = () => setTurnstileError('Failed to load anti-spam captcha.');
      document.head.appendChild(s);
    } else {
      if (window.turnstile?.render) setTurnstileReady(true);
      else {
        // wait until the global is available
        const t = setInterval(() => {
          if (window.turnstile?.render) {
            clearInterval(t);
            setTurnstileReady(true);
          }
        }, 100);
        return () => clearInterval(t);
      }
    }
  }, [turnstileSiteKey]);

  useEffect(() => {
    if (!turnstileSiteKey) return;
    if (!turnstileReady) return;
    if (step !== 5) return;              // ✅ only when the UI is visible
    if (!turnstileElRef.current) return;

    // render once per mount
    if (turnstileWidRef.current != null) return;

    try {
      if (!window.turnstile?.render) {
        setTurnstileError('Captcha script loaded but Turnstile is unavailable (blocked?).');
        return;
      }

      const wid = window.turnstile.render(turnstileElRef.current, {
        sitekey: turnstileSiteKey,
        theme: 'light',
        callback: token => setTurnstileToken(token || ''),
        'expired-callback': () => setTurnstileToken(''),
        'error-callback': () => {
          setTurnstileToken('');
          setTurnstileError('Captcha error. Please retry.');
        },
      });

      turnstileWidRef.current = wid;
    } catch (e) {
      setTurnstileError('Failed to initialize captcha.');
    }
  }, [turnstileSiteKey, turnstileReady, step]); // ✅ add step


  function resetTurnstile() {
    try {
      if (turnstileWidRef.current != null && window.turnstile) {
        window.turnstile.reset(turnstileWidRef.current);
      }
    } catch {
      // ignore
    }
    setTurnstileToken('');
  }

  /* -------------------- date constraints -------------------- */
  const countryCode = useMemo(() => locationCodeFromCountry(country), [country]);
  const tz = useMemo(() => timeZoneForCountryCode(countryCode), [countryCode]);
  const isAlbaniaReservation = useMemo(() => safeStr(countryCode).toUpperCase() === 'AL', [countryCode]);
  const crossBorderCountriesLabel = useMemo(() => CROSS_BORDER_ALLOWED_COUNTRIES.join(', '), []);

  useEffect(() => {
    if (isAlbaniaReservation) return;
    setExtras(prev => (
      prev?.crossBorder || normalizeCrossBorderCountries(prev?.crossBorderCountries).length
        ? { ...prev, crossBorder: false, crossBorderCountries: [] }
        : prev
    ));
  }, [isAlbaniaReservation]);

  const toggleCrossBorderCountry = useCallback((countryName, checked) => {
    setExtras(prev => {
      const current = normalizeCrossBorderCountries(prev?.crossBorderCountries);
      const next = checked
        ? Array.from(new Set([...current, countryName]))
        : current.filter(c => c !== countryName);
      return {
        ...prev,
        crossBorder: next.length > 0,
        crossBorderCountries: next,
      };
    });
  }, []);

  const nowMinLocal = useMemo(() => {
    // now in destination tz, then + lead hours in destination tz
    const base = nowLocalInputInZone(tz);
    // snap minutes/seconds however you want; keeping your “hour rounded” behavior:
    const [dPart, tPart] = base.split("T");
    const hh = (tPart || "00:00").slice(0, 2);
    const rounded = `${dPart}T${hh}:00`;
    return addHoursLocalInputInZone(rounded, MIN_START_LEAD_HOURS, tz);
  }, [tz]);


  const endMinLocal = useMemo(() => {
    if (!startLocal) return addHoursLocalInputInZone(nowMinLocal, 24 * MIN_RENTAL_DAYS, tz) || nowMinLocal;
    return addHoursLocalInputInZone(startLocal, 24 * MIN_RENTAL_DAYS, tz) || nowMinLocal;
  }, [startLocal, nowMinLocal, tz]);

  useEffect(() => {
    if (!startLocal || !endLocal) return;

    try {
      const sMs = fromZonedTime(startLocal, tz).getTime();
      const nowMs = fromZonedTime(nowMinLocal, tz).getTime();

      if (Number.isFinite(sMs) && Number.isFinite(nowMs) && sMs < nowMs) {
        setStartLocal(nowMinLocal);
        return;
      }

      // end must be >= start + MIN_RENTAL_DAYS (in tz)
      const minEndLocal = addHoursLocalInputInZone(startLocal, 24 * MIN_RENTAL_DAYS, tz);
      const minEndMs = fromZonedTime(minEndLocal, tz).getTime();
      const eMs = fromZonedTime(endLocal, tz).getTime();

      if (!Number.isFinite(eMs) || eMs < minEndMs) {
        setEndLocal(endMinLocal);
      }
    } catch {
      // fail-soft: do nothing
    }
  }, [startLocal, endLocal, nowMinLocal, endMinLocal, tz]);


  const companyForCountry = useMemo(() => pickBestCompany(companies || [], country), [companies, country]);
  const currencyCode = BACKEND_CURRENCY; // backend always USD


  /* -------------------- Load addons pricing -------------------- */
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        setAddonsErr(null);
        setAddonsPricing(null);
        if (!companyForCountry?.id || !countryCode) return;
        setAddonsLoading(true);

        const res = await client.graphql({
          query: ADDONS_BY_COMPANY_BY_COUNTRY,
          variables: {
            CompanyId: companyForCountry.id,
            countryCode: { eq: countryCode },
          },
          authMode: await smartAuthMode(),
        });

        const items = res?.data?.addonsByCompanyByCountry?.items || [];
        if (!items.length) {
          if (!cancelled) setAddonsPricing(null);
          return;
        }

        const sorted = [...items].sort((a, b) => {
          const ta = new Date(a?.updatedAt || a?.createdAt || 0).getTime();
          const tb = new Date(b?.updatedAt || b?.createdAt || 0).getTime();
          return tb - ta;
        });
        if (!cancelled) setAddonsPricing(sorted[0]);
      } catch (e) {
        if (!cancelled) setAddonsErr(e?.message || 'Failed to load extras pricing.');
      } finally {
        if (!cancelled) setAddonsLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [companyForCountry?.id, countryCode]);

  const pickupOptions = useMemo(() => {
    const base = Array.isArray(companyForCountry?.pickupLocations) ? companyForCountry.pickupLocations : [];
    return base;
  }, [companyForCountry]);

  const dropoffOptions = useMemo(() => {
    const base = Array.isArray(companyForCountry?.dropoffLocations) ? companyForCountry.dropoffLocations : [];
    return base;
  }, [companyForCountry]);


  const startISO = useMemo(() => {
    try { return toISOInZone(startLocal, tz); } catch { return ""; }
  }, [startLocal, tz]);

  const endISO = useMemo(() => {
    try { return toISOInZone(endLocal, tz); } catch { return ""; }
  }, [endLocal, tz]);


  const rentalDays = useMemo(() => {
    if (!startISO || !endISO) return 0;
    return calcRentalDays(startISO, endISO);
  }, [startISO, endISO]);

  const carsForCountry = useMemo(() => {
    if (!companyForCountry) return [];
    return (cars || []).filter(cc => cc.CompanyId === companyForCountry.id && cc.active);
  }, [cars, companyForCountry]);

  const deliveryFees = useMemo(() => {
    return computeDeliveryFee(countryCode, pickupMode, dropoffMode);
  }, [countryCode, pickupMode, dropoffMode]);

  const deliveryFeesUsd = useMemo(() => {
    return toUSD(deliveryFees.total || 0, deliveryFees.currency || 'USD', fxRates);
  }, [deliveryFees.total, deliveryFees.currency, fxRates]);

  const availableGroups = useMemo(() => {
    const map = new Map();
    for (const car of availableCars) {
      const key = normalizeGroupKey(car?.name);
      if (!key) continue;
      if (!map.has(key)) {
        map.set(key, { groupKey: key, groupName: safeStr(car.name) || 'Unnamed', variants: [] });
      } else {
        const cur = map.get(key);
        const incoming = safeStr(car.name);
        if (incoming && incoming.length > safeStr(cur.groupName).length) cur.groupName = incoming;
      }
      map.get(key).variants.push(car);
    }

    const groups = Array.from(map.values()).map(g => {
      g.variants.sort((a, b) => safeStr(a.id).localeCompare(safeStr(b.id)));
      return g;
    });

    groups.sort((a, b) => {
      const ta = groupCheapestTotal(a.variants, quotesByKey, insuranceTier, extras, addonsPricing, rentalDays, deliveryFeesUsd, countryCode, fxRates);
      const tb = groupCheapestTotal(b.variants, quotesByKey, insuranceTier, extras, addonsPricing, rentalDays, deliveryFeesUsd, countryCode, fxRates);

      // priced groups first
      if (ta !== tb) return ta - tb;

      // stable fallback
      return safeStr(a.groupName).localeCompare(safeStr(b.groupName));
    });
    return groups;
  }, [fxRates, availableCars, quotesByKey, insuranceTier, extras, addonsPricing, rentalDays, deliveryFeesUsd, countryCode]);


  function clearAvailabilityUI({ keepStep = false } = {}) {
    setAvailableCars([]);
    setSelectedCar(null);
    setOpenGroupKey(null);
    setAvailabilityErr(null);
    if (!keepStep) goToStep(1);
    setQuotesByKey({});
    setSubmitErr(null);
    setSubmitOk(null);
  }

  const areaList = useMemo(() => {
    if (countryCode === 'GE') return TBILISI_AREAS;
    if (countryCode === 'AL') return TIRANA_AREAS;
    return [];
  }, [countryCode]);

  const pickupFinal = useMemo(() => {
    if (pickupMode === 'FREE') return safeStr(pickupFree);
    const a = safeStr(pickupArea);
    return a ? `Delivery: ${a}` : '';
  }, [pickupMode, pickupFree, pickupArea]);

  const dropoffFinal = useMemo(() => {
    if (dropoffMode === 'FREE') return safeStr(dropoffFree);
    const a = safeStr(dropoffArea);
    return a ? `Delivery: ${a}` : '';
  }, [dropoffMode, dropoffFree, dropoffArea]);

  useEffect(() => {
    if (!didMountTripRef.current) {
      didMountTripRef.current = true;
      return;
    }

    // if user edits any trip field (except country), wipe step 2+ and require re-check
    invalidateAfterTripChange();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    startLocal,
    endLocal,
    pickupMode,
    dropoffMode,
    pickupFree,
    dropoffFree,
    pickupArea,
    dropoffArea,
    pickupExactNote,
    dropoffExactNote,
  ]);

  const canCheckAvailability =
    !dataLoading &&
    !searching &&
    safeStr(country) &&
    safeStr(startLocal) &&
    safeStr(endLocal) &&
    rentalDays >= MIN_RENTAL_DAYS &&
    safeStr(pickupFinal) &&
    safeStr(dropoffFinal) &&
    // if delivery mode, require area
    (pickupMode !== 'DELIVERY' || safeStr(pickupArea)) &&
    (dropoffMode !== 'DELIVERY' || safeStr(dropoffArea));
  const canGo2 =
    !dataLoading &&
    safeStr(country) &&
    safeStr(startLocal) &&
    safeStr(endLocal) &&
    safeStr(pickupFinal) &&
    safeStr(dropoffFinal) &&
    rentalDays >= MIN_RENTAL_DAYS &&
    availableGroups.length > 0;

  const canGo3 = canGo2 && !!selectedCar;
  const canGo4 = canGo3;
  const canGo5 =
    canGo4 &&
    safeStr(fullName) &&
    safeStr(email) &&
    safeStr(phonePrimary) &&
    safeStr(payerCountryCode);

  /* -------------------- load images for available cars -------------------- */
  useEffect(() => {
    let cancelled = false;
    async function loadImages() {
      const missing = availableCars.filter(c => c?.imageKey && !carImageUrls[c.id]);
      if (!missing.length) return;

      missing.forEach(async (car) => {
        try {
          const raw = safeStr(car.imageKey);
          const key = raw.startsWith('public/') ? raw : `public/${raw}`;
          const urlRes = await getUrl({
            path: key,
            options: { validateObjectExistence: false },
          });
          const url = urlRes?.url?.toString?.() || urlRes?.url;
          if (!cancelled && url) {
            setCarImageUrls(prev => (prev[car.id] ? prev : { ...prev, [car.id]: url }));
          }
        } catch {
          // ignore
        }
      });
    }
    loadImages();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableCars]);

  /* -------------------- availability search -------------------- */
  async function checkAvailability() {
    setNeedsRecheck(false);
    hasSearchedRef.current = true;
    setSearching(true);
    setAvailabilityErr(null);
    setAvailableCars([]);
    setSelectedCar(null);
    setOpenGroupKey(null);
    setQuotesByKey({});
    setSubmitErr(null);
    setSubmitOk(null);

    try {
      if (!companyForCountry) throw new Error(`No company configured for ${country}.`);
      if (!countryCode) throw new Error('Invalid country selection.');

      const authMode = await smartAuthMode();
      const sISO = toISOInZone(startLocal, tz);
      const eISO = toISOInZone(endLocal, tz);

      if (new Date(sISO).getTime() >= new Date(eISO).getTime()) throw new Error('End date must be after start date.');
      if (new Date(sISO).getTime() < Date.now()) throw new Error('Start date cannot be in the past.');

      const days = calcRentalDays(sISO, eISO);
      if (days < MIN_RENTAL_DAYS) throw new Error(`Minimum rental is ${MIN_RENTAL_DAYS} days.`);

      if (pickupMode === 'DELIVERY' && !safeStr(pickupArea)) throw new Error('Pick-up delivery area is required.');
      if (pickupMode === 'FREE' && !safeStr(pickupFree)) throw new Error('Pick-up location is required.');

      if (dropoffMode === 'DELIVERY' && !safeStr(dropoffArea)) throw new Error('Drop-off delivery area is required.');
      if (dropoffMode === 'FREE' && !safeStr(dropoffFree)) throw new Error('Drop-off location is required.');

      const widenMs = 1000 * 60 * 60 * 24 * 365;
      const windowStart = new Date(new Date(sISO).getTime() - widenMs).toISOString();
      const windowEnd = eISO;

      const availability = [];
      for (const car of carsForCountry) {
        const [rRes, mRes] = await Promise.all([
          client.graphql({
            query: RES_BY_CAR_BY_START,
            variables: { carId: car.id, start: { between: [windowStart, windowEnd] }, limit: 200 },
            authMode,
          }),
          client.graphql({
            query: MAINT_BY_CAR_BY_START,
            variables: { carId: car.id, start: { between: [windowStart, windowEnd] }, limit: 200 },
            authMode,
          }),
        ]);

        const reservations = rRes?.data?.reservationsByCarByStart?.items || [];
        const maints = mRes?.data?.maintenanceByCarByStart?.items || [];

        const blocks = [];
        for (const r of reservations) {
          const isBlocking =
            (r?.approvalStatus || 'APPROVED') === 'APPROVED' &&
            !['DECLINED', 'CANCELED'].includes(r?.status || '');
          if (isBlocking) blocks.push({ start: r.start, end: r.end });
        }
        for (const m of maints) {
          const isBlocking = (m?.approvalStatus || 'APPROVED') === 'APPROVED';
          if (isBlocking) blocks.push({ start: m.start, end: m.end });
        }

        const isAvailable = !blocks.some(b => overlaps(sISO, eISO, b.start, b.end));
        if (isAvailable) availability.push(car);
      }

      setAvailableCars(availability);
      goToStep(2);

    } catch (e) {
      console.error(e);
      setAvailabilityErr(e?.message || 'Availability check failed.');
    } finally {
      setSearching(false);
    }
  }

  /* -------------------- Backend-priced quote -------------------- */
  async function ensureQuote(carId, insuranceTierValue, extrasObj) {
    const k = quoteKey(carId, insuranceTierValue, extrasObj);

    const existing = quotesByKey[k];
    if (existing?.quote?.quoteId && !existing.loading) return existing.quote;
    if (existing?.loading) return null;

    setQuotesByKey(prev => ({
      ...prev,
      [k]: { loading: true, err: null, quote: prev?.[k]?.quote || null },
    }));

    try {
      if (!companyForCountry?.id) throw new Error('No company selected.');
      if (!countryCode) throw new Error('Invalid country selection.');
      if (!startISO || !endISO) throw new Error('Start/end dates are required.');
      if (rentalDays < MIN_RENTAL_DAYS) throw new Error(`Minimum rental is ${MIN_RENTAL_DAYS} days.`);
      if (new Date(startISO).getTime() < new Date().getTime()) throw new Error('Start date cannot be in the past.');
      if (!pickupFinal || !dropoffFinal) throw new Error('Pickup/dropoff locations are required.');
      const normalizedExtras = normalizeExtras(extrasObj);
      if (normalizedExtras.crossBorder && safeStr(countryCode).toUpperCase() !== 'AL') {
        throw new Error('Cross-border add-on is available only for Albania reservations.');
      }
      if (normalizedExtras.crossBorder && !normalizedExtras.crossBorderCountries.length) {
        throw new Error('Select each cross-border country you intend to enter.');
      }

      const res = await client.graphql({
        query: QUOTE_PUBLIC_RESERVATION,
        variables: {
          input: {
            CompanyId: companyForCountry.id,
            carId,
            start: startISO,
            end: endISO,
            countryCode,
            wantInsurance: safeStr(insuranceTierValue) === 'FULL',
            extras: JSON.stringify(normalizedExtras),
          },
        },
        authMode: await smartAuthMode(),
      });

      const q = res?.data?.quotePublicReservation;
      if (!q?.quoteId) throw new Error('Invalid quote response from backend.');

      let breakdownObj = null;

      try {
        const bj = q?.breakdownJson;

        // AWSJSON may arrive as an object already (NOT a JSON string)
        if (bj && typeof bj === 'object') {
          breakdownObj = bj;
        } else if (typeof bj === 'string' && bj.trim()) {
          breakdownObj = JSON.parse(bj);
        } else {
          breakdownObj = null;
        }
      } catch {
        breakdownObj = null;
      }

      const manual = breakdownObj?.status === 'MANUAL_QUOTE_REQUIRED';

      const parsed = {
        ...q,
        currency: BACKEND_CURRENCY, // force USD
        total: Number.isFinite(Number(q.total)) ? Number(q.total) : 0,
        payableNow: Number.isFinite(Number(q.payableNow)) ? Number(q.payableNow) : 0,
        breakdown: breakdownObj,
        manualQuote: manual,
      };

      setQuotesByKey(prev => ({
        ...prev,
        [k]: { loading: false, err: null, quote: parsed },
      }));

      return parsed;
    } catch (e) {
      const msg = e?.message || 'Failed to compute price.';
      setQuotesByKey(prev => ({
        ...prev,
        [k]: { loading: false, err: msg, quote: null },
      }));
      return null;
    }
  }

  async function prequoteAllCarsForOptions(insuranceTierValue, extrasValue) {
    if (!availableCars?.length) return;

    const carIds = availableCars.map(c => c.id).filter(Boolean);
    const limit = 4;
    let idx = 0;

    async function worker() {
      while (idx < carIds.length) {
        const my = idx++;
        const carId = carIds[my];
        await ensureQuote(carId, insuranceTierValue, extrasValue);
      }
    }

    await Promise.all(Array.from({ length: Math.min(limit, carIds.length) }, worker));
  }

  useEffect(() => {
    if (step !== 2) return;
    if (!availableCars?.length) return;

    const t = setTimeout(() => {
      prequoteAllCarsForOptions(insuranceTier, extras);
    }, 250);

    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, availableCars, insuranceTier, extras, startISO, endISO, countryCode, companyForCountry?.id, pickupFinal, dropoffFinal]);

  useEffect(() => {
    if (!selectedCar?.id) return;
    if (!startISO || !endISO) return;
    if (rentalDays < MIN_RENTAL_DAYS) return;
    if (!pickupFinal || !dropoffFinal) return;
    ensureQuote(selectedCar.id, insuranceTier, extras);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCar?.id, insuranceTier, extras, startISO, endISO, rentalDays, pickupFinal, dropoffFinal, countryCode, companyForCountry?.id]);

  const policyDeposit = useMemo(() => {
    if (!selectedCar) return null;

    const isAL = safeStr(countryCode).toUpperCase() === 'AL';
    const full = safeStr(insuranceTier) === 'FULL';
    if (isAL && full) return { amount: 0, currency: 'EUR' }; // or keep EUR for clarity

    return calcPolicyDeposit(countryCode, selectedCar?.category);
  }, [countryCode, selectedCar, insuranceTier]);


  const clientAddonsTotal = useMemo(() => {
    const base = calcClientAddonsTotal(
      addonsPricing,
      insuranceTier,
      extras,
      rentalDays,
      { countryCode, bodyType: selectedCar?.bodyType }
    );
    const apCur = addonsMoneyCurrencyFor(countryCode, addonsPricing);
    const usd = toUSD(base, apCur, fxRates);
    const crossBorderUsd = crossBorderFeeUsdForExtras(extras, countryCode);
    return Math.max(0, Math.round(Number(usd) + crossBorderUsd));
  }, [addonsPricing, insuranceTier, extras, rentalDays, fxRates, countryCode, selectedCar?.bodyType]);
  const effectiveQuote = useMemo(() => {
    if (!quote) return null;

    const adjustedBreakdown = applyCrossBorderCountryPricingToBreakdown(quote?.breakdown, extras, countryCode);
    const backendCrossBorderAdjustment = getCrossBorderAddonsAdjustment(quote?.breakdown, extras, countryCode);
    const backendAddons = getBackendAddonsTotalFromBreakdown(adjustedBreakdown);
    const backendIncludesAddons = Number.isFinite(Number(backendAddons));

    const addonsUsed = backendIncludesAddons
      ? Number(backendAddons)
      : Number(clientAddonsTotal);

    const quoteTotalWithAddons = backendIncludesAddons
      ? Number(quote.total) + Number(backendCrossBorderAdjustment || 0)
      : Number(quote.total) + Number(addonsUsed);

    const total = quoteTotalWithAddons + Number(deliveryFeesUsd || 0);

    const basePayableNow = Number(quote.payableNow);
    const payableNowFinal =
      Number.isFinite(basePayableNow) && basePayableNow > 0
        ? Number(roundMoney(basePayableNow))
        : Math.max(0, Number(roundMoney(total) ?? 0));

    const policyDepAmount = policyDeposit?.amount;
    const policyDepCurrency = policyDeposit?.currency;
    const hasPolicyDep = Number.isFinite(Number(policyDepAmount));

    return {
      ...quote,
      breakdown: adjustedBreakdown || quote.breakdown,
      total: Number.isFinite(total) ? Number(roundMoney(total) ?? total) : quote.total,
      payableNow: payableNowFinal, // ✅ this is what you should charge online
      addonsTotal: addonsUsed,
      addonsSource: backendIncludesAddons ? 'backend' : 'client_estimate',
      policyDeposit: hasPolicyDep ? { amount: Number(policyDepAmount), currency: policyDepCurrency || 'USD' } : null,
    };
  }, [quote, extras, countryCode, clientAddonsTotal, deliveryFeesUsd, policyDeposit]);

  const payNowUsd = useMemo(() => {
    if (!effectiveQuote || effectiveQuote.manualQuote) return null;
    const p = Number(effectiveQuote.payableNow);
    return Number.isFinite(p) && p > 0 ? Number(roundMoney(p) ?? p) : null;
  }, [effectiveQuote]);

  async function submitReservationRequest({ silent = false } = {}) {
    setSubmitLoading(true);
    if (!silent) {
      setSubmitErr(null);
      setSubmitOk(null);
    }

    try {
      if (!companyForCountry?.id) throw new Error('No company selected.');
      if (!countryCode) throw new Error('Invalid country selection.');
      if (!selectedCar?.id) throw new Error('Select a car.');
      if (!startISO || !endISO) throw new Error('Start/end dates are required.');
      if (rentalDays < MIN_RENTAL_DAYS) throw new Error(`Minimum rental is ${MIN_RENTAL_DAYS} days.`);
      if (new Date(startISO).getTime() < new Date().getTime()) throw new Error('Start date cannot be in the past.');
      if (!safeStr(fullName) || !safeStr(email) || !safeStr(phonePrimary)) throw new Error('Missing customer details.');
      if (!safeStr(pickupFinal) || !safeStr(dropoffFinal)) throw new Error('Pickup/dropoff locations are required.');
      if (!termsAccepted) throw new Error('Please accept the Online Reservation Terms & Conditions before continuing.');

      // --- anti-spam checks ---
      if (safeStr(hpField)) throw new Error('Spam detected.');
      if (turnstileSiteKey && !safeStr(turnstileToken)) throw new Error('Please complete the anti-spam captcha.');
      const normalizedExtras = normalizeExtras(extras);
      if (normalizedExtras.crossBorder && safeStr(countryCode).toUpperCase() !== 'AL') {
        throw new Error('Cross-border add-on is available only for Albania reservations.');
      }
      if (normalizedExtras.crossBorder && !normalizedExtras.crossBorderCountries.length) {
        throw new Error('Select each cross-border country you intend to enter.');
      }

      const rawQuote = await ensureQuote(selectedCar.id, insuranceTier, extras);
      const hasQuote = !!rawQuote?.quoteId;

      const backendBreakdownRaw = rawQuote?.breakdown || null;
      const backendBreakdown = applyCrossBorderCountryPricingToBreakdown(backendBreakdownRaw, normalizedExtras, countryCode);
      const backendCrossBorderAdjustment = getCrossBorderAddonsAdjustment(backendBreakdownRaw, normalizedExtras, countryCode);
      const normalizedBreakdown = normalizeBreakdown(backendBreakdown);
      const backendAddons = getBackendAddonsTotalFromBreakdown(backendBreakdown);
      const addonsTotal = Number.isFinite(Number(backendAddons)) ? Number(backendAddons) : clientAddonsTotal;

      const policyDepAmount = policyDeposit?.amount;
      const hasPolicyDep = Number.isFinite(Number(policyDepAmount));
      const policyDep = hasPolicyDep ? Number(policyDepAmount) : 0; // deposit-on-arrival policy

      const total = hasQuote
        ? (Number.isFinite(Number(backendAddons))
          ? Number(rawQuote.total) + Number(backendCrossBorderAdjustment || 0)
          : Number(rawQuote.total) + Number(addonsTotal))
        : null;

      const breakdownInsuranceTotal = Number(normalizedBreakdown?.addons?.lines?.insurance?.total);
      const fallbackInsuranceTotal =
        safeStr(insuranceTier) === 'FULL'
          ? insurancePerDayForReservation(addonsPricing, rentalDays, countryCode, selectedCar?.bodyType) * rentalDays
          : 0;
      const insuranceFeeTotal = Number.isFinite(breakdownInsuranceTotal)
        ? breakdownInsuranceTotal
        : fallbackInsuranceTotal;
      const extrasFeeTotal = Math.max(0, Number(addonsTotal || 0) - Number(insuranceFeeTotal || 0));
      const rentalTotal = Number.isFinite(Number(normalizedBreakdown?.baseTotal))
        ? Number(normalizedBreakdown.baseTotal)
        : (Number.isFinite(Number(total)) ? Math.max(0, Number(total) - Number(addonsTotal || 0)) : null);
      const siteDiscount = getSiteDiscountSummary(backendBreakdown);
      const rentalTotalBeforeDiscount = Number.isFinite(Number(siteDiscount?.rentalBefore))
        ? Number(siteDiscount.rentalBefore)
        : rentalTotal;
      const rentalDiscountAmount = Number.isFinite(Number(siteDiscount?.discountAmount))
        ? Number(siteDiscount.discountAmount)
        : 0;
      const totalBeforeDiscount = Number.isFinite(Number(siteDiscount?.totalBefore))
        ? Number(siteDiscount.totalBefore)
        : (Number.isFinite(Number(total)) ? Number(total) + Number(rentalDiscountAmount || 0) : null);

      const payAmount =
        !rawQuote?.manualQuote && effectiveQuote
          ? Number(roundMoney(effectiveQuote.payableNow || 0) ?? 0)
          : 0;

      const provider = paymentProviderFor(payerCountryCode);
      const selectedCarMeta = {
        name: safeStr(selectedCar.name) || null,
        year: carModelYear(selectedCar) || null,
        plate: safeStr(selectedCar.plate) || null,
        transmission: safeStr(selectedCar.transmission) || null,
        fuelType: safeStr(selectedCar.fuelType) || null,
        category: safeStr(selectedCar.category) || null,
        bodyType: safeStr(selectedCar.bodyType) || null,
        seats: selectedCar.seats ?? null,
      };

      const payload = {
        antiSpam: {
          provider: turnstileSiteKey ? 'turnstile' : 'none',
          token: turnstileSiteKey ? safeStr(turnstileToken) : null,
          honeypot: safeStr(hpField) || null,
          formTs,
          pageUrl: typeof window !== 'undefined' ? window.location.href : null,
          userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        },
        countryCode,

        payment: {
          phoneCountryCode: safeStr(payerCountryCode) || null,
          provider, // 'MANUAL' | 'MPGS'
          currency: 'USD',
          amountUsd: provider === 'MANUAL' ? 0 : payAmount,
        },

        pickup: {
          mode: pickupMode,
          freeLocation: pickupMode === 'FREE' ? safeStr(pickupFree) : null,
          area: pickupMode === 'DELIVERY' ? safeStr(pickupArea) : null,
          note: pickupMode === 'DELIVERY' ? safeStr(pickupExactNote) || null : null,
          fee: deliveryFees.pickupFee || 0,
        },
        dropoff: {
          mode: dropoffMode,
          freeLocation: dropoffMode === 'FREE' ? safeStr(dropoffFree) : null,
          area: dropoffMode === 'DELIVERY' ? safeStr(dropoffArea) : null,
          note: dropoffMode === 'DELIVERY' ? safeStr(dropoffExactNote) || null : null,
          fee: deliveryFees.dropoffFee || 0,
        },
        deliveryFeesTotal: deliveryFeesUsd || 0,
        selectedCarMeta,
        extras: normalizedExtras,
        wantInsurance: safeStr(insuranceTier) === 'FULL',
        flightNumber: safeStr(flightNumber) || null,
        message: safeStr(custMessage) || null,
        onlineReservationTerms: {
          title: ONLINE_RESERVATION_TERMS_TITLE,
          version: ONLINE_RESERVATION_TERMS_VERSION,
          lastUpdated: ONLINE_RESERVATION_TERMS_LAST_UPDATED,
          checkboxLabel: ONLINE_RESERVATION_TERMS_CHECKBOX_LABEL,
          accepted: true,
          acceptedAt: new Date().toISOString(),
          sections: ONLINE_RESERVATION_TERMS_SECTIONS,
        },
        pricing: hasQuote
          ? {
            quoteId: rawQuote.quoteId,
            currency: rawQuote.currency || currencyCode,
            rentalDays,
            total: Number.isFinite(Number(total)) ? Number(total) : Number(rawQuote.total),
            totalBeforeDiscount: roundMoney(totalBeforeDiscount),
            rentalTotal: roundMoney(rentalTotal),
            rentalTotalBeforeDiscount: roundMoney(rentalTotalBeforeDiscount),
            rentalDiscountAmount: roundMoney(rentalDiscountAmount),
            rentalDiscountRate: Number(siteDiscount?.rate || 0),
            pricingOfferLabel: siteDiscount?.label || SITE_RESERVATION_DISCOUNT_LABEL,
            insuranceFeeTotal: roundMoney(insuranceFeeTotal),
            extrasFeeTotal: roundMoney(extrasFeeTotal),

            // charge online only if MPGS
            payableNow: provider === 'MANUAL' ? 0 : (Number.isFinite(Number(payAmount)) ? Number(payAmount) : 0),
            payableNowCurrency: BACKEND_CURRENCY,

            policyDepositAmount: policyDep,
            policyDepositCurrency: policyDeposit?.currency || 'USD',

            depositPolicyApplied: hasPolicyDep,
            addonsTotal,
            addonsSource: Number.isFinite(Number(backendAddons)) ? 'backend' : 'client_estimate',
            breakdown: backendBreakdown,
            manualQuote: !!rawQuote.manualQuote,
          }
          : {
            manualQuote: true,
            reason: 'QUOTE_FAILED_OR_NOT_CONFIGURED',
            currency: currencyCode,
            rentalDays,
            rentalTotal: roundMoney(rentalTotal),
            insuranceFeeTotal: roundMoney(insuranceFeeTotal),
            extrasFeeTotal: roundMoney(extrasFeeTotal),
            payableNow: 0,
            payableNowCurrency: BACKEND_CURRENCY,

            policyDepositAmount: policyDep,
            policyDepositCurrency: policyDeposit?.currency || 'USD',

            depositPolicyApplied: hasPolicyDep,
            addonsTotal,
            addonsSource: 'client_estimate',
          },
      };

      const res = await client.graphql({
        query: SUBMIT_INBOUND_REQUEST,
        variables: {
          input: {
            CompanyId: companyForCountry.id,
            type: 'PUBLIC_RESERVATION',

            name: safeStr(fullName),
            email: safeStr(email),
            phone: safeStr(phonePrimary),
            whatsapp: safeStr(phoneWhatsapp) || safeStr(phonePrimary),
            country: safeStr(country),

            start: startISO,
            end: endISO,
            pickupLocation: pickupFinal,
            dropoffLocation: dropoffFinal,

            selectedCarId: selectedCar.id,
            selectedCarName: formatCarNameWithoutYear(selectedCar, ''),
            selectedCarPlate: safeStr(selectedCar.plate),
            selectedCarMeta: JSON.stringify(selectedCarMeta),

            message: safeStr(custMessage) || null,
            payload: JSON.stringify(payload),
            source: 'landingpage',
          },
        },
        authMode: await smartAuthMode(),
      });

      const created = res?.data?.submitInboundRequest;
      if (!created?.id) throw new Error('Reservation submission failed.');

      if (!silent) {
        setSubmitOk({ id: created.id, status: created.status });
      }
      resetTurnstile();

      // return BOTH the inboundRequest id and the payload used
      return { inboundRequestId: created.id, payload };
    } catch (e) {
      console.error(e);
      const msg = e?.message || 'Failed to submit reservation request.';
      if (!silent) setSubmitErr(msg);
      throw e;
    } finally {
      setSubmitLoading(false);
    }
  }

  async function proceedToPayment() {
    setPayErr(null);
    setSubmitErr(null);
    setSubmitOk(null);

    if (isManualPhone) {
      try {
        await submitReservationRequest({ silent: false });
      } catch (e) {
        // submitReservationRequest already sets submitErr
      }
      return;
    }

    // hard gate: no payment if manual quote
    if (!effectiveQuote || effectiveQuote.manualQuote) {
      setSubmitErr('This car requires a manual quote. Payment is not available.');
      return;
    }

    const amount = Number(payNowUsd);
    if (!Number.isFinite(amount) || amount <= 0) {
      setSubmitErr('Payment amount is invalid.');
      return;
    }

    // prevent double-click / double redirect
    if (redirectingPay || payStatus === 'creating' || payStatus === 'redirecting') return;

    try {
      setRedirectingPay(true);

      // create inbound request first (lead)
      const { inboundRequestId } = await submitReservationRequest({ silent: true });

      // prevent double redirect using inbound request id
      if (didRedirectFor(inboundRequestId)) {
        setRedirectingPay(false);
        return;
      }
      markRedirectFor(inboundRequestId);

      await startMpgsHostedCheckout({ inboundRequestId, amount, currency: 'USD' });
      return;
    } catch (e) {
      console.error(e);
      setPayErr(e?.message || 'Failed to start payment.');
      setPayStatus('failed');
      setRedirectingPay(false);
    }
  }

  const apCur = addonsMoneyCurrencyFor(countryCode, addonsPricing);
  const showManualSubmit = isManualPhone || effectiveQuote?.manualQuote;
  const actionRowStyle = isMobile
    ? { display: 'grid', gap: 10 }
    : { display: 'flex', gap: 10, flexWrap: 'wrap' };
  const actionPrimaryStyle = isMobile
    ? { ...ctaPrimary, width: '100%', justifyContent: 'center' }
    : ctaPrimary;
  const actionSecondaryStyle = isMobile
    ? { ...ctaSecondary, width: '100%', textAlign: 'center' }
    : ctaSecondary;
  const seatExtraRows = useMemo(() => ([
    { key: 'boosterSeat', label: 'Booster seat', priceField: 'boosterSeatPerDay' },
    { key: 'infantSeat', label: 'Infant seat', priceField: 'infantSeatPerDay' },
    { key: 'babySeat', label: 'Baby seat', priceField: 'babySeatPerDay' },
  ]), []);

  const seatExtraCount = useCallback((key) => {
    return normalizeSeatExtraCount(extras?.[`${key}Count`], !!extras?.[key]);
  }, [extras]);

  const setSeatExtraCount = useCallback((key, value) => {
    const count = normalizeSeatExtraCount(value);
    setExtras(prev => ({
      ...prev,
      [key]: count > 0,
      [`${key}Count`]: count,
    }));
  }, []);

  const currentStepMeta = RESERVATION_STEP_META[step - 1] || RESERVATION_STEP_META[0];
  const summaryExtras = normalizeExtras(extras);
  const summaryExtrasCount =
    summaryExtras.boosterSeatCount +
    summaryExtras.infantSeatCount +
    summaryExtras.babySeatCount +
    summaryExtras.extraDriversCount +
    (summaryExtras.snowChains ? 1 : 0) +
    summaryExtras.crossBorderCountries.length;
  const summaryQuoteTotal = Number(effectiveQuote?.total);
  const hasSummaryQuote =
    !!effectiveQuote && !effectiveQuote.manualQuote && Number.isFinite(summaryQuoteTotal);
  const summaryCarLabel = selectedCar
    ? formatCarNameWithoutYearPlate(selectedCar, 'Selected vehicle')
    : 'Your trip at a glance';

  /* -------------------- UI -------------------- */
  return (
    <div ref={reservationTopRef} className="rf-shell">
      <div className="rf-panel">
        <div className="rf-panel-header">
          <div className="rf-header-layout">
            <div className="rf-booking-heading">
              <div className="rf-booking-icon">
                <CarFront size={18} />
              </div>
              <div>
                <div className="rf-booking-kicker">Haste online booking</div>
                <h3>{currentStepMeta.title}</h3>
                <p>{currentStepMeta.description}</p>
                <div style={{ fontSize: 12, color: '#6b7280' }}>Trip → Car → Insurance & extras → Your info → Review</div>
              </div>
            </div>

            <div className="rf-panel-toolbar">
              <Stepper
                step={step}
                setStep={goToStep}
                canGo2={canGo2}
                canGo3={canGo3}
                canGo4={canGo4}
                canGo5={canGo5}
              />

              <div className="rf-currency-bar">
                <div className="rf-currency-label">Currency</div>
                <div className="rf-currency-select">
                  <SelectPopover
                    value={displayCurrency}
                    onChange={setDisplayCurrency}
                    options={DISPLAY_CURRENCY_OPTIONS.map(o => ({ value: o.code, label: o.label }))}
                    searchable={false}
                    placeholder="Currency…"
                    maxMenuHeight={240}
                  />
                </div>
                <div className={`rf-fx-status ${fxErr ? 'is-error' : ''}`}>
                  {fxLoading ? 'FX…' : fxErr ? 'FX err' : 'FX ok'}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="rf-mobile-summary" aria-live="polite">
          <span>{safeStr(country) || 'Plan your trip'}</span>
          <strong>{rentalDays ? `${rentalDays} days` : 'Choose dates'}</strong>
          <span>{hasSummaryQuote ? fmt(summaryQuoteTotal, 'USD') : selectedCar ? 'Price pending' : 'No car selected'}</span>
        </div>

        <div className="rf-flow-layout">
          <main className="rf-flow-main">
        {/* ================= STEP 1: TRIP ================= */}
        {step === 1 && (
          <div className="rf-step-content rf-trip-step">
            <div className="rf-step-lead">
              <span>Trip details</span>
              <h4>Where and when do you need a car?</h4>
              <p>Enter your route once and we will only show vehicles available for the full rental.</p>
            </div>

            <div className="rf-field-grid" style={grid2}>
              <SelectPopover
                labelText="Country"
                value={country}
                onChange={v => {
                  setCountry(v);
                  resetTripFieldsForCountry(v);
                }}
                options={companyCountryOptions}
                searchable={false}
              />

              <FancyDateRangePicker
                labelText="Trip dates"
                startValue={startLocal}
                endValue={endLocal}
                onStartChange={setStartLocal}
                onEndChange={setEndLocal}
                startMin={nowMinLocal}
                endMin={endMinLocal}
                minRentalDays={MIN_RENTAL_DAYS}
                tz={tz}
              />
            </div>

            <div className="rf-location-grid">
              <div className="rf-location-card">
                <div className="rf-location-label"><span>1</span> Pick-up location</div>
                <LocationModeToggle
                  value={pickupMode}
                  deliveryFeeLabel={countryCode === 'GE'
                    ? fmt(GEO_DELIVERY_FEE_PER_LEG, 'USD')
                    : countryCode === 'AL'
                      ? fmt(AL_DELIVERY_FEE_PER_LEG, 'USD')
                      : '—'}
                  onChange={(mode) => {
                    if (mode === 'FREE') {
                      setPickupMode('FREE');
                      setPickupArea('');
                      setPickupExactNote('');
                    } else {
                      setPickupMode('DELIVERY');
                      setPickupFree('');
                    }
                  }}
                />

                {pickupMode === 'FREE' ? (
                  <SelectPopover
                    value={pickupFree}
                    onChange={setPickupFree}
                    options={pickupOptions}
                    placeholder="Select location…"
                    searchPlaceholder="Search locations…"
                  />
                ) : (
                  <div className="rf-location-fields">
                    <SelectPopover
                      value={pickupArea}
                      onChange={setPickupArea}
                      options={areaList}
                      placeholder="Select area…"
                      searchPlaceholder={countryCode === 'GE' ? 'Search Tbilisi areas…' : 'Search Tirana areas…'}
                    />
                    <input
                      value={pickupExactNote}
                      onChange={e => setPickupExactNote(e.target.value)}
                      className="rf-location-input"
                      placeholder="Exact address / note (optional)"
                    />
                    <div className="rf-location-hint">
                      {countryCode === 'GE' ? 'Delivery is only within Tbilisi areas.' : 'Delivery is only within Tirana.'}
                    </div>
                  </div>
                )}
              </div>

              <div className="rf-location-card">
                <div className="rf-location-label"><span>2</span> Drop-off location</div>
                <LocationModeToggle
                  value={dropoffMode}
                  deliveryFeeLabel={countryCode === 'GE'
                    ? fmt(GEO_DELIVERY_FEE_PER_LEG, 'USD')
                    : countryCode === 'AL'
                      ? fmt(AL_DELIVERY_FEE_PER_LEG, 'USD')
                      : '—'}
                  onChange={(mode) => {
                    if (mode === 'FREE') {
                      setDropoffMode('FREE');
                      setDropoffArea('');
                      setDropoffExactNote('');
                    } else {
                      setDropoffMode('DELIVERY');
                      setDropoffFree('');
                    }
                  }}
                />

                {dropoffMode === 'FREE' ? (
                  <SelectPopover
                    value={dropoffFree}
                    onChange={setDropoffFree}
                    options={dropoffOptions}
                    placeholder="Select location…"
                    searchPlaceholder="Search locations…"
                  />
                ) : (
                  <div className="rf-location-fields">
                    <SelectPopover
                      value={dropoffArea}
                      onChange={setDropoffArea}
                      options={areaList}
                      placeholder="Select area…"
                      searchPlaceholder={countryCode === 'GE' ? 'Search Tbilisi areas…' : 'Search Tirana areas…'}
                    />
                    <input
                      value={dropoffExactNote}
                      onChange={e => setDropoffExactNote(e.target.value)}
                      className="rf-location-input"
                      placeholder="Exact address / note (optional)"
                    />
                    <div className="rf-location-hint">
                      {countryCode === 'GE' ? 'Delivery is only within Tbilisi areas.' : 'Delivery is only within Tirana.'}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {availabilityErr ? (
              <div className="rf-trip-alert" role="alert">{availabilityErr}</div>
            ) : null}

            <div className="rf-check-copy">
              <strong>Ready to see what is available?</strong>
              <span>Prices include the online rental discount where available.</span>
            </div>

            <button
              className="rf-check-btn"
              type="button"
              disabled={!canCheckAvailability}
              aria-busy={searching}
              onClick={checkAvailability}
            >
              {searching ? (
                <span className="rf-check-btn-content">
                  <Loader2 size={16} className="spin" /> Checking…
                </span>
              ) : (
                <span className="rf-check-btn-content">
                  <CalendarDays size={16} /> Check availability
                </span>
              )}
            </button>
          </div>
        )}

        {/* ================= STEP 2: CAR ================= */}
        {step === 2 && (
          <div className="rf-step-content" style={{ display: 'grid', gap: 12 }}>
            <div className="rf-step-lead">
              <span>{availableGroups.length} available {availableGroups.length === 1 ? 'group' : 'groups'}</span>
              <h4>{availableGroups.length ? 'Choose the car that fits your trip' : 'No cars available for this trip'}</h4>
              <p>
                {availableGroups.length
                  ? 'Select a group, then fine-tune transmission, fuel, and body type.'
                  : 'We could not find an available car for the exact dates you selected.'}
              </p>
            </div>

            <div style={{ display: 'grid', gap: 12 }}>
              {!availableGroups.length ? (
                <section className="rf-empty-availability" role="status" aria-live="polite">
                  <div className="rf-empty-availability-visual" aria-hidden="true">
                    <CarFront size={32} />
                    <span><CalendarDays size={16} /></span>
                  </div>

                  <div className="rf-empty-availability-copy">
                    <span className="rf-empty-availability-kicker">Let&apos;s try another route</span>
                    <h5>A small change may open up more options.</h5>
                    <p>
                      Try adjusting your rental dates, then check availability again.
                    </p>
                  </div>

                  <div className="rf-empty-availability-suggestions" aria-label="Suggestions">
                    <div>
                      <CalendarDays size={18} aria-hidden="true" />
                      <span><strong>Try nearby dates</strong>Shifting your trip may reveal more cars.</span>
                    </div>
                    <div>
                      <Clock3 size={18} aria-hidden="true" />
                      <span><strong>Change the trip length</strong>A shorter or later rental may fit the schedule.</span>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => goToStep(1)}
                    className="rf-cta rf-cta-primary rf-empty-availability-action"
                  >
                    <CalendarDays size={16} /> Adjust your trip
                  </button>
                </section>
              ) : availableGroups.map(g => {
                const isOpen = openGroupKey === g.groupKey;
                const groupSummary = (() => {
                  const quoteEntries = g.variants.map(variant => ({
                    variant,
                    entry: quotesByKey[quoteKey(variant.id, insuranceTier, extras)],
                  }));

                  const entries = quoteEntries
                    .map(item => item.entry)
                    .filter(Boolean);
                  const quotes = entries.map(e => e?.quote).filter(Boolean);

                  const anyLoading =
                    quoteEntries.some(item => item.entry?.loading) || quotes.length < quoteEntries.length;
                  const anyErr = entries.find(e => e?.err)?.err || null;

                  if (!quotes.length) {
                    return { loading: anyLoading, err: anyErr, fromTotal: null, fromDeposit: null };
                  }

                  let best = null;
                  for (const { variant, entry } of quoteEntries) {
                    const q = entry?.quote;
                    if (!q) continue;

                    const backendCrossBorderAdjustment = getCrossBorderAddonsAdjustment(q?.breakdown, extras, countryCode);
                    const adjustedBreakdown = applyCrossBorderCountryPricingToBreakdown(q?.breakdown, extras, countryCode);
                    const breakdownAddons = getBackendAddonsTotalFromBreakdown(adjustedBreakdown);
                    const addons = Number.isFinite(Number(breakdownAddons))
                      ? Number(breakdownAddons)
                      : (() => {
                        const est = calcClientAddonsTotal(
                          addonsPricing,
                          insuranceTier,
                          extras,
                          rentalDays,
                          {
                            countryCode,
                            bodyType: variant?.bodyType || q?.breakdown?.bodyType,
                          }
                        );
                        const apCur = addonsMoneyCurrencyFor(countryCode, addonsPricing);
                        const baseUsd = toUSD(est, apCur, fxRates);
                        const crossBorderUsd = crossBorderFeeUsdForExtras(extras, countryCode);
                        return Number(baseUsd || 0) + crossBorderUsd;
                      })();

                    const assumeBackendIncludesAddons = Number.isFinite(Number(breakdownAddons));
                    const quoteTotal = assumeBackendIncludesAddons
                      ? Number(q.total) + Number(backendCrossBorderAdjustment || 0)
                      : Number(q.total) + Number(addons);
                    const total = quoteTotal + Number(deliveryFeesUsd || 0);
                    const siteDiscount = getSiteDiscountSummary(adjustedBreakdown);
                    const totalBeforeDiscount = Number.isFinite(Number(siteDiscount?.totalBefore))
                      ? Number(siteDiscount.totalBefore) + Number(deliveryFeesUsd || 0)
                      : null;

                    const dep =
                      calcDepositAmount(countryCode, variant?.category, insuranceTier) ??
                      calcDepositAmount(countryCode, q?.breakdown?.category, insuranceTier);

                    const deposit = Number.isFinite(Number(dep)) ? Number(dep) : Number(q.payableNow);

                    const cand = {
                      currency: q.currency || currencyCode,
                      total: Number.isFinite(total) ? Number(roundMoney(total) ?? total) : Number(q.total),
                      originalTotal: Number.isFinite(Number(totalBeforeDiscount))
                        ? Number(roundMoney(totalBeforeDiscount) ?? totalBeforeDiscount)
                        : null,
                      hasDiscount: !!siteDiscount?.hasDiscount,
                      deposit,
                    };

                    if (!best || cand.total < best.total) best = cand;
                  }

                  return {
                    loading: anyLoading,
                    err: anyErr,
                    fromTotal: Number(best.total),
                    fromOriginalTotal: Number.isFinite(Number(best.originalTotal)) ? Number(best.originalTotal) : null,
                    hasDiscount: !!best.hasDiscount,
                    fromDeposit: Number(best.deposit),
                    currency: best.currency || currencyCode,
                  };
                })();

                return (
                  <div key={g.groupKey}>
                    <GroupAvailabilityCard
                      groupName={g.groupName}
                      variants={g.variants}
                      carImageUrls={carImageUrls}
                      isOpen={isOpen}
                      isMobile={isMobile}
                      selectedCar={selectedCar}
                      setSelectedCar={setSelectedCar}
                      onToggle={() => {
                        if (isOpen) return setOpenGroupKey(null);
                        setOpenGroupKey(g.groupKey);
                        setSubmitErr(null);
                        setSubmitOk(null);
                      }}
                      rentalDays={rentalDays}
                      priceSummary={groupSummary}
                      formatMoney={fmt}
                      currencyCode={BACKEND_CURRENCY}
                      onBack={() => goToStep(1)}
                      onContinue={() => goToStep(3)}
                      canContinue={!!selectedCar && g.variants.some(v => v?.id === selectedCar?.id)}
                      countryCode={countryCode}
                    />
                  </div>
                );
              })}
            </div>

            {!openGroupKey && availableGroups.length > 0 && (
              <div className="rf-action-row" style={actionRowStyle}>
                <button type="button" onClick={() => goToStep(1)} className="rf-cta rf-cta-secondary" style={actionSecondaryStyle}>
                  Back
                </button>
                <button
                  type="button"
                  disabled={!canGo3}
                  onClick={() => goToStep(3)}
                  className="rf-cta rf-cta-primary"
                  style={{ ...actionPrimaryStyle, opacity: canGo3 ? 1 : 0.55, cursor: canGo3 ? 'pointer' : 'not-allowed' }}
                >
                  Continue <ArrowRight size={16} />
                </button>
              </div>
            )}
          </div>
        )}

        {/* ================= STEP 3: INSURANCE & EXTRAS ================= */}
        {step === 3 && (
          <div className="rf-step-content rf-options-step">
            <div className="rf-step-lead">
              <span>Coverage and extras</span>
              <h4>Make the rental yours</h4>
              <p>Compare protection clearly, then add only what you will use.</p>
            </div>

            <div className="rf-step3-context" aria-label="Current rental">
              <span className="rf-step3-context-icon" aria-hidden="true">
                <ShieldCheck size={20} />
              </span>
              <div className="rf-step3-context-copy">
                <small>Customizing</small>
                <strong>{summaryCarLabel}</strong>
              </div>
              <div className="rf-step3-context-meta">
                <Clock3 size={16} aria-hidden="true" />
                <span>
                  <strong>{rentalDays || '--'}</strong>
                  {rentalDays === 1 ? ' day' : ' days'}
                </span>
              </div>
            </div>

            <div style={{ marginTop: 10, display: 'grid', gap: 12 }}>
              <div className="rf-card-elevate rf-section-card rf-insurance-section" style={reviewCard}>
                <div className="rf-option-section-heading" style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                  <div className="rf-option-section-title">
                    <span className="rf-option-section-number">01</span>
                    <span>
                      <strong>Choose your protection</strong>
                      <small>One clear choice for the whole trip</small>
                    </span>
                  </div>

                  {/* Small context chips */}
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <SpecChip>{rentalDays ? `${rentalDays} day(s)` : 'Days —'}</SpecChip>
                    {safeStr(countryCode).toUpperCase() === 'AL' ? (
                      <SpecChip tone="good">Albania: Full coverage = No deposit</SpecChip>
                    ) : null}
                  </div>
                </div>

                {addonsLoading ? (
                  <div style={{ marginTop: 10, fontSize: 12, color: '#6b7280', fontWeight: 700 }}>
                    Loading insurance pricing…
                  </div>
                ) : addonsErr ? (
                  <div style={{ marginTop: 10, ...alertBad }}>{addonsErr}</div>
                ) : null}

                {(() => {
                  const isAL = safeStr(countryCode).toUpperCase() === 'AL';
                  const isGE = safeStr(countryCode).toUpperCase() === 'GE';
                  const perDay = insurancePerDayForReservation(
                    addonsPricing,
                    rentalDays,
                    countryCode,
                    selectedCar?.bodyType
                  );
                  const apCur2 = addonsMoneyCurrencyFor(countryCode, addonsPricing);
                  const fullSelected = safeStr(insuranceTier) === 'FULL';

                  const depositIfNoFull = Number.isFinite(
                    Number(calcDepositAmount(countryCode, selectedCar?.category, insuranceTier))
                  )
                    ? Number(calcDepositAmount(countryCode, selectedCar?.category, insuranceTier))
                    : null;

                  // UI copy: “sell” full coverage hard, with Albania deposit removal called out
                  const fullHeadline = isAL
                    ? 'Full coverage (Recommended) — No deposit in Albania'
                    : 'Full coverage (Recommended)';

                  const fullSub = isAL
                    ? 'You pay the daily coverage fee, and your deposit requirement drops to zero.'
                    : isGE
                      ? 'The daily coverage price is calculated from the selected vehicle type.'
                      : 'You pay the daily coverage fee to reduce your risk and stress.';

                  const thirdSub =
                    'Cheapest option. You are still responsible for damage to the rented vehicle.';

                  const showFullPrice = Number.isFinite(Number(perDay)) && Number(perDay) > 0;

                  const cardBase = selected => ({
                    border: selected ? `2px solid ${BRAND_GREEN_DARK}` : '1px solid #e5e7eb',
                    borderRadius: 8,
                    background: selected
                      ? `linear-gradient(180deg, rgba(126,217,87,0.18), rgba(255,255,255,0.98))`
                      : 'linear-gradient(180deg, #ffffff, #f8fafc)',
                    padding: 14,
                    cursor: 'pointer',
                    boxShadow: selected ? `0 20px 48px ${BRAND_GREEN_SHADOW}` : '0 12px 28px rgba(15,23,42,0.08)',
                    transition: 'transform 120ms ease, box-shadow 120ms ease',
                  });

                  const row = {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                    padding: '10px 12px',
                    borderRadius: 8,
                    border: '1px solid #e5e7eb',
                    background: '#ffffff',
                  };

                  const rowL = { fontSize: 12, fontWeight: 950, color: '#6b7280' };
                  const rowR = { fontSize: 13, fontWeight: 900, color: '#111827', textAlign: 'right' };

                  return (
                    <>
                      <div
                        style={{
                          marginTop: 10,
                          display: 'grid',
                          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(240px, 1fr))',
                          gap: 12,
                        }}
                      >
                        {/* THIRD PARTY */}
                        <label className={`rf-insurance-option rf-protection-basic ${(insuranceTier || 'THIRD_PARTY') === 'THIRD_PARTY' ? 'is-selected' : ''}`} style={cardBase((insuranceTier || 'THIRD_PARTY') === 'THIRD_PARTY')}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <input
                              type="radio"
                              name="insuranceTier"
                              checked={(insuranceTier || 'THIRD_PARTY') === 'THIRD_PARTY'}
                              onChange={() => setInsuranceTier('THIRD_PARTY')}
                            />
                            <div style={{ display: 'grid', gap: 2 }}>
                              <div style={{ fontWeight: 950, color: '#111827' }}>
                                Third-party <span style={{ color: '#6b7280', fontWeight: 900 }}>(Free)</span>
                              </div>
                              <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 800, lineHeight: 1.35 }}>
                                {thirdSub}
                              </div>
                            </div>
                          </div>

                          <div style={{ marginTop: 12, display: 'grid', gap: 8 }}>
                            <div style={row}>
                              <div style={rowL}>Covers</div>
                              <div style={rowR}>Damage you cause to others</div>
                            </div>
                            <div style={row}>
                              <div style={rowL}>Doesn’t cover</div>
                              <div style={rowR}>Damage to the rented vehicle</div>
                            </div>
                            <div style={row}>
                              <div style={rowL}>Price</div>
                              <div style={rowR}>0 / day</div>
                            </div>
                          </div>

                          <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            <SpecChip tone="warn">Highest risk</SpecChip>
                            <SpecChip>Lowest cost</SpecChip>
                            {isAL ? <SpecChip>Deposit required</SpecChip> : <SpecChip>Deposit required</SpecChip>}
                          </div>
                        </label>

                        {/* FULL */}
                        <label className={`rf-insurance-option rf-protection-full ${fullSelected ? 'is-selected' : ''}`} style={cardBase(fullSelected)}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <input
                              type="radio"
                              name="insuranceTier"
                              checked={fullSelected}
                              onChange={() => setInsuranceTier('FULL')}
                            />
                            <div style={{ display: 'grid', gap: 2 }}>
                              <div style={{ fontWeight: 950, color: '#111827' }}>
                                {fullHeadline}{' '}
                                {showFullPrice ? (
                                  <span style={{ color: '#374151', fontWeight: 900 }}>
                                    ({fmt(perDay, apCur2)}/day)
                                  </span>
                                ) : (
                                  <span style={{ color: '#6b7280', fontWeight: 900 }}>(price not set)</span>
                                )}
                              </div>
                              <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 800, lineHeight: 1.35 }}>
                                {fullSub}
                              </div>
                            </div>
                          </div>

                          <div style={{ marginTop: 12, display: 'grid', gap: 8 }}>
                            <div style={row}>
                              <div style={rowL}>Covers</div>
                              <div style={rowR}>Rental car damage (terms apply)</div>
                            </div>
                            <div style={row}>
                              <div style={rowL}>Benefit</div>
                              <div style={rowR}>Reduces your liability</div>
                            </div>
                            <div style={row}>
                              <div style={rowL}>Price</div>
                              <div style={rowR}>
                                {showFullPrice ? (
                                  <>
                                    {fmt(perDay, apCur2)} / day
                                    {rentalDays ? (
                                      <div style={{ marginTop: 2, fontSize: 12, fontWeight: 900, color: '#6b7280' }}>
                                        Est. {fmt(Number(perDay) * Number(rentalDays), apCur2)} total
                                      </div>
                                    ) : null}
                                  </>
                                ) : (
                                  'Per-day price not set'
                                )}
                              </div>
                            </div>

                            {/* Deposit callout (hard sell) */}
                            {isAL ? (
                              <div
                                style={{
                                  borderRadius: 8,
                                  border: `1px solid ${BRAND_GREEN_SOFT_BORDER}`,
                                  background: `linear-gradient(180deg, ${BRAND_GREEN_SOFT}, #ffffff)`,
                                  padding: 12,
                                  color: '#2f5f1f',
                                  fontWeight: 900,
                                  fontSize: 13,
                                  boxShadow: '0 12px 28px rgba(15,23,42,0.08)',
                                }}
                              >
                                Albania: selecting Full coverage sets your deposit to <b>0</b>.
                              </div>
                            ) : Number.isFinite(Number(depositIfNoFull)) ? (
                              <div
                                style={{
                                  borderRadius: 8,
                                  border: '1px solid #e5e7eb',
                                  background: '#ffffff',
                                  padding: 12,
                                  color: '#111827',
                                  fontWeight: 850,
                                  fontSize: 13,
                                }}
                              >
                                Deposit still applies by policy: <b>{fmt(Number(depositIfNoFull), 'USD')}</b>.
                              </div>
                            ) : null}
                          </div>

                          <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            <SpecChip tone="good">Recommended</SpecChip>
                            <SpecChip tone="good">{isAL ? 'No deposit' : 'Lower risk'}</SpecChip>
                            <SpecChip>Fewer surprises</SpecChip>
                          </div>
                        </label>
                      </div>
                    </>
                  );
                })()}
              </div>



              <div className="rf-card-elevate rf-section-card rf-extras-section" style={reviewCard}>
                <div className="rf-option-section-heading rf-extras-heading">
                  <div className="rf-option-section-title">
                    <span className="rf-option-section-number">02</span>
                    <span>
                      <strong>Add useful extras</strong>
                      <small>Skip anything you do not need</small>
                    </span>
                  </div>
                  <span className="rf-selected-count">
                    {summaryExtrasCount ? `${summaryExtrasCount} selected` : 'None selected'}
                  </span>
                </div>

                {addonsLoading ? (
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 8 }}>Loading extras prices…</div>
                ) : addonsErr ? (
                  <div style={alertBad}>{addonsErr}</div>
                ) : null}

                <div className="rf-extras-grid" style={{ marginTop: 10, ...extrasGrid }}>
                  {seatExtraRows.map(({ key, label: seatLabel, priceField }) => {
                    const count = seatExtraCount(key);
                    return (
                      <div
                        key={key}
                        className={`rf-extra-quantity ${count > 0 ? 'is-selected' : ''}`}
                        style={{
                          display: 'grid',
                          gap: 8,
                          gridTemplateColumns: '1fr',
                          alignItems: 'center',
                          padding: '10px 12px',
                          borderRadius: 8,
                          border: '1px solid rgba(17, 24, 39, 0.08)',
                          background: '#fff',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                          <div
                            style={{
                              fontSize: 13,
                              color: '#111827',
                              fontWeight: 800,
                              display: 'grid',
                              gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 1fr) auto',
                              alignItems: 'center',
                              gap: isMobile ? 8 : 14,
                              width: '100%',
                            }}
                          >
                            <span>
                              {seatLabel}
                              {Number.isFinite(Number(addonsPricing?.[priceField])) ? (
                                <span style={{ fontSize: 12, fontWeight: 900, color: '#374151', marginLeft: 8 }}>
                                  ({fmt(Number(addonsPricing[priceField]), apCur)}/day each)
                                </span>
                              ) : null}
                            </span>

                            <span
                              style={{
                                display: 'grid',
                                gridTemplateColumns: '40px 68px 40px',
                                alignItems: 'center',
                                justifyContent: isMobile ? 'start' : 'end',
                                gap: 8,
                              }}
                            >
                              <button
                                className="rf-pill-btn rf-qty-minus"
                                type="button"
                                onClick={() => setSeatExtraCount(key, count - 1)}
                                style={{ ...qtyBtn, width: 40, height: 40, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}
                                aria-label={`Decrease ${seatLabel}`}
                              >
                                -
                              </button>

                              <input
                                type="number"
                                min={0}
                                max={MAX_SEAT_EXTRA_COUNT}
                                step={1}
                                value={count}
                                onChange={e => setSeatExtraCount(key, e.target.value)}
                                style={{ ...inputStyle, height: 40, width: 68, borderRadius: 8, padding: '0 8px', textAlign: 'center', fontWeight: 900 }}
                                aria-label={`${seatLabel} quantity`}
                              />

                              <button
                                className="rf-pill-btn"
                                type="button"
                                onClick={() => setSeatExtraCount(key, count + 1)}
                                style={{ ...qtyBtn, width: 40, height: 40, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}
                                aria-label={`Increase ${seatLabel}`}
                              >
                                +
                              </button>
                            </span>
                          </div>
                        </div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280' }}>
                          Choose 0-{MAX_SEAT_EXTRA_COUNT}. Maximum {MAX_SEAT_EXTRA_COUNT}.
                        </div>
                      </div>
                    );
                  })}

                  <label className={`rf-extra-toggle-card ${extras?.snowChains ? 'is-selected' : ''}`} style={extraRow}>
                    <input
                      type="checkbox"
                      checked={!!extras?.snowChains}
                      onChange={e => setExtras(prev => ({ ...prev, snowChains: e.target.checked }))}
                    />
                    Snow chains{' '}
                    {Number.isFinite(Number(addonsPricing?.snowChainsPerDay)) ? (
                      <span style={{ fontWeight: 900, color: '#374151' }}>
                        ({fmt(Number(addonsPricing.snowChainsPerDay), apCur)}/day)
                      </span>
                    ) : null}
                  </label>

                  <div className={`rf-cross-border-card ${normalizeCrossBorderCountries(extras?.crossBorderCountries).length ? 'is-selected' : ''}`} style={{ display: 'grid', gap: 8 }}>
                    <div style={{ ...extraRow, alignItems: 'flex-start' }}>
                      <span style={{ fontWeight: 900 }}>Cross-border permit</span>{' '}
                      {isAlbaniaReservation ? (
                        <span style={{ fontWeight: 900, color: '#374151' }}>
                          ({fmt(CROSS_BORDER_FEE_USD, 'USD')} per country)
                        </span>
                      ) : (
                        <span style={{ fontWeight: 900, color: '#9ca3af' }}>(Not available for this rental)</span>
                      )}
                    </div>
                    {isAlbaniaReservation ? (
                      <div style={{ display: 'grid', gap: 6, paddingLeft: 2 }}>
                        {CROSS_BORDER_ALLOWED_COUNTRIES.map(countryName => {
                          const selectedCountries = normalizeCrossBorderCountries(extras?.crossBorderCountries);
                          const checked = selectedCountries.includes(countryName);
                          return (
                            <label key={countryName} style={{ ...extraRow, fontSize: 12 }}>
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={e => toggleCrossBorderCountry(countryName, e.target.checked)}
                              />
                              {countryName}
                            </label>
                          );
                        })}
                      </div>
                    ) : null}
                  </div>
                  <div className="rf-cross-border-note" style={extraNote}>
                    {isAlbaniaReservation
                      ? `Select every country you intend to enter. Allowed countries: ${crossBorderCountriesLabel}.`
                      : 'Cross-border travel is not available for this rental country.'}
                  </div>

                  <div
                    className={`rf-extra-quantity rf-extra-driver ${Number(extras?.extraDriversCount) > 0 ? 'is-selected' : ''}`}
                    style={{
                      display: 'grid',
                      gap: 8,
                      gridTemplateColumns: '1fr',
                      padding: '10px 12px',
                      borderRadius: 8,
                      border: '1px solid rgba(17, 24, 39, 0.08)',
                      background: '#fff',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      <div
                        style={{
                          fontSize: 13,
                          color: '#111827',
                          fontWeight: 800,
                          display: 'grid',
                          gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 1fr) auto',
                          alignItems: 'center',
                          gap: isMobile ? 8 : 14,
                          width: '100%',
                        }}
                      >
                        <span>
                          Extra drivers
                          {Number.isFinite(Number(addonsPricing?.extraDriverPerDay)) ? (
                            <span style={{ fontSize: 12, fontWeight: 900, color: '#374151', marginLeft: 8 }}>
                              ({fmt(Number(addonsPricing.extraDriverPerDay), apCur)}/day)
                            </span>
                          ) : null}
                        </span>

                        <span
                          style={{
                            display: 'grid',
                            gridTemplateColumns: '40px 68px 40px',
                            alignItems: 'center',
                            justifyContent: isMobile ? 'start' : 'end',
                            gap: 8,
                          }}
                        >
                          <button
                            className="rf-pill-btn rf-qty-minus"
                            type="button"
                            onClick={() =>
                              setExtras(prev => ({
                                ...prev,
                                extraDriversCount: Math.max(0, Math.min(10, (Number(prev?.extraDriversCount) || 0) - 1)),
                              }))
                            }
                            style={{ ...qtyBtn, width: 40, height: 40, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}
                            aria-label="Decrease extra drivers"
                          >
                            -
                          </button>

                          <input
                            type="number"
                            min={0}
                            max={10}
                            step={1}
                            value={Number(extras?.extraDriversCount) || 0}
                            aria-label="Extra drivers quantity"
                            onChange={e => {
                              const v = Math.max(0, Math.min(10, Math.floor(Number(e.target.value) || 0)));
                              setExtras(prev => ({ ...prev, extraDriversCount: v }));
                            }}
                            style={{ ...inputStyle, height: 40, width: 68, borderRadius: 8, padding: '0 8px', textAlign: 'center', fontWeight: 900 }}
                          />

                          <button
                            className="rf-pill-btn"
                            type="button"
                            onClick={() =>
                              setExtras(prev => ({
                                ...prev,
                                extraDriversCount: Math.max(0, Math.min(10, (Number(prev?.extraDriversCount) || 0) + 1)),
                              }))
                            }
                            style={{ ...qtyBtn, width: 40, height: 40, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}
                            aria-label="Increase extra drivers"
                          >
                            +
                          </button>

                        </span>
                      </div>
                    </div>

                    <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280' }}>Set to 0 if none.</div>
                  </div>
                </div>
              </div>

              <div className="rf-step3-selection-bar" aria-live="polite">
                <div>
                  <span>Your setup</span>
                  <strong>
                    {insuranceTier === 'FULL' ? 'Full coverage' : 'Third-party coverage'}
                    {' / '}
                    {summaryExtrasCount
                      ? `${summaryExtrasCount} extra ${summaryExtrasCount === 1 ? 'item' : 'items'}`
                      : 'No extras'}
                  </strong>
                  <small>Changes are reflected in your reservation total.</small>
                </div>
                <div className="rf-step3-addon-total">
                  <span>Coverage + extras</span>
                  <strong>{addonsLoading ? 'Calculating...' : fmt(clientAddonsTotal, 'USD')}</strong>
                </div>
              </div>

              <div className="rf-action-row" style={actionRowStyle}>
                <button type="button" onClick={() => goToStep(2)} className="rf-cta rf-cta-secondary" style={actionSecondaryStyle}>
                  Back
                </button>
                <button type="button" onClick={() => goToStep(4)} className="rf-cta rf-cta-primary" style={actionPrimaryStyle}>
                  Continue to your details <ArrowRight size={16} />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ================= STEP 4: YOUR INFO ================= */}
        {step === 4 && (
          <div className="rf-step-content">
            <div className="rf-step-lead">
              <span>Driver details</span>
              <h4>Who should we prepare the car for?</h4>
              <p>We will use these details only to coordinate this reservation.</p>
            </div>

            <div className="rf-section-card rf-contact-card" style={{ marginTop: 10, ...reviewCard }}>
              <div className="rf-contact-grid" style={grid2}>
                <label style={label}>
                  Full name *
                  <input value={fullName} onChange={e => setFullName(e.target.value)} style={inputStyle} placeholder="Your full name" autoComplete="name" />
                </label>

                <label style={label}>
                  Email *
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} style={inputStyle} placeholder="you@example.com" autoComplete="email" />
                </label>
                <label style={label}>
                  Phone *
                  <div
                    className="rf-phone-field"
                    style={{
                      border: '1px solid rgba(17,24,39,0.12)',
                      borderRadius: 8,
                      background: '#ffffff',
                      boxShadow: '0 1px 0 rgba(15,23,42,0.04)',
                      height: 48,
                      display: 'flex',
                      alignItems: 'center',
                      overflow: 'hidden',
                    }}
                  >
                    {/* code segment */}
                    <div
                      style={{
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0 8px 0 10px',
                        borderRight: '1px solid rgba(17,24,39,0.12)',
                        background: 'linear-gradient(180deg, #ffffff, #f8fafc)',
                      }}
                    >
                      {/* IMPORTANT: we visually “shrink” the SelectPopover by giving it a compact wrapper */}
                      <div style={{ width: isMobile ? 102 : 120 }}>
                        <SelectPopover
                          value={payerCountryCode}
                          onChange={setPayerCountryCode}
                          options={PHONE_COUNTRY_CODES}
                          searchable
                          placeholder="+Code"
                          searchPlaceholder="Search…"
                          maxMenuHeight={260}
                          compact
                        />
                      </div>
                    </div>

                    {/* number segment */}
                    <input
                      value={phonePrimaryDigits}
                      onChange={e => setPhonePrimaryDigits(e.target.value)}
                      inputMode="numeric"
                      autoComplete="tel-national"
                      placeholder="Phone number"
                      style={{
                        flex: 1,
                        height: '100%',
                        border: 'none',
                        outline: 'none',
                        padding: '0 12px',
                        fontSize: 13,
                        fontWeight: 850,
                        background: 'transparent',
                        color: '#111827',
                      }}
                    />
                  </div>
                </label>


                <label style={label}>
                  WhatsApp
                  <input
                    value={phoneWhatsapp}
                    onChange={e => setPhoneWhatsapp(e.target.value)}
                    style={inputStyle}
                    placeholder="WhatsApp (optional)"
                  />
                </label>

                <label style={label}>
                  Flight number
                  <input
                    value={flightNumber}
                    onChange={e => setFlightNumber(e.target.value)}
                    style={inputStyle}
                    placeholder="Optional"
                  />
                </label>

                <label style={label}>
                  Message
                  <textarea
                    value={custMessage}
                    onChange={e => setCustMessage(e.target.value)}
                    style={inputStyle}
                    rows={3}
                    placeholder="Anything we should know? (optional)"
                  />
                </label>
              </div>
            </div>

            <div className="rf-action-row" style={{ ...actionRowStyle, marginTop: 12 }}>
              <button type="button" onClick={() => goToStep(3)} className="rf-cta rf-cta-secondary" style={actionSecondaryStyle}>
                Back
              </button>

              <button
                type="button"
                onClick={() => goToStep(5)}
                disabled={!safeStr(fullName) || !safeStr(email) || !safeStr(phonePrimary) || !safeStr(payerCountryCode)}
                className="rf-cta rf-cta-primary"
                style={{
                  ...actionPrimaryStyle,
                  opacity: safeStr(fullName) && safeStr(email) && safeStr(phonePrimary) ? 1 : 0.55,
                  cursor: safeStr(fullName) && safeStr(email) && safeStr(phonePrimary) ? 'pointer' : 'not-allowed',
                }}
              >
                Continue <ArrowRight size={16} />
              </button>
            </div>
          </div>
        )}

        {/* ================= STEP 5: REVIEW ================= */}
        {step === 5 && (
          <div className="rf-step-content">
            <div className="rf-step-lead">
              <span>Final check</span>
              <h4>Review your reservation</h4>
              <p>Confirm the trip, price, and contact details before you continue.</p>
            </div>

            <div className="rf-card-elevate rf-section-card rf-review-section" style={reviewCard}>
              <div style={{ display: 'grid', gap: 10, fontSize: 13 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                  <div style={{ fontWeight: 950, color: '#111827' }}>Trip summary</div>
                  <SpecChip>
                    {rentalDays ? `${rentalDays} day(s)` : '—'} · {safeStr(country)} ({countryCode || '—'})
                  </SpecChip>
                </div>

                <div style={reviewGrid}>
                  <div style={reviewItem}>
                    <div style={reviewLabel}>Pick-up</div>
                    <div style={reviewValue}>{safeStr(pickupFinal) || '—'}</div>
                  </div>
                  <div style={reviewItem}>
                    <div style={reviewLabel}>Drop-off</div>
                    <div style={reviewValue}>{safeStr(dropoffFinal) || '—'}</div>
                  </div>
                  <div style={reviewItem}>
                    <div style={reviewLabel}>Start</div>
                    <div style={reviewValue}>{formatDateTimeNice(startLocal, tz)}</div>
                  </div>
                  <div style={reviewItem}>
                    <div style={reviewLabel}>End</div>
                    <div style={reviewValue}>{formatDateTimeNice(endLocal, tz)}</div>
                  </div>
                </div>

                <Divider style={{ margin: '6px 0' }} />

                <div style={{ fontWeight: 950, color: '#111827' }}>Car & options</div>
                <div style={reviewGrid}>
                  <div style={reviewItem}>
                    <div style={reviewLabel}>Selected car</div>
                    <div style={reviewValue}>
                      {selectedCar ? formatCarNameWithoutYearPlate(selectedCar, '') : '—'}
                    </div>
                  </div>
                  <div style={reviewItem}>
                    <div style={reviewLabel}>Delivery fees</div>
                    <div style={reviewValue}>
                      {deliveryFees.total > 0
                        ? `${fmt(deliveryFees.total, deliveryFees.currency)} (${deliveryFees.pickupFee > 0 ? 'pickup' : ''}${deliveryFees.pickupFee > 0 && deliveryFees.dropoffFee > 0 ? ' + ' : ''}${deliveryFees.dropoffFee > 0 ? 'dropoff' : ''})`
                        : 'None'}
                    </div>
                  </div>

                  <div style={reviewItem}>
                    <div style={reviewLabel}>Insurance</div>
                    <div style={reviewValue}>{insuranceTier === 'FULL' ? 'Full coverage' : 'Third-party (free)'}</div>
                  </div>

                  <div style={reviewItem}>
                    <div style={reviewLabel}>Extras</div>
                    <div style={reviewValue}>
                      {(() => {
                        const ex = normalizeExtras(extras);
                        const list = [];
                        if (ex.boosterSeatCount > 0) list.push(ex.boosterSeatCount === 1 ? 'Booster seat' : `Booster seat (${ex.boosterSeatCount})`);
                        if (ex.infantSeatCount > 0) list.push(ex.infantSeatCount === 1 ? 'Infant seat' : `Infant seat (${ex.infantSeatCount})`);
                        if (ex.babySeatCount > 0) list.push(ex.babySeatCount === 1 ? 'Baby seat' : `Baby seat (${ex.babySeatCount})`);
                        if (ex.snowChains) list.push('Snow chains');
                        if (ex.crossBorder && ex.crossBorderCountries.length) {
                          list.push(
                            `Cross-border permit (${fmt(CROSS_BORDER_FEE_USD * ex.crossBorderCountries.length, 'USD')}; ${ex.crossBorderCountries.join(', ')})`
                          );
                        }
                        if (ex.extraDriversCount > 0) list.push(`Extra drivers: ${ex.extraDriversCount}`);
                        return list.length ? list.join(' · ') : 'None';
                      })()}
                    </div>
                  </div>

                  <div style={reviewItem}>
                    <div style={reviewLabel}>Currency</div>
                    <div style={reviewValue}>
                      Pricing: {BACKEND_CURRENCY} · Display: {safeStr(displayCurrency).toUpperCase()}
                    </div>
                  </div>
                </div>

                <Divider style={{ margin: '6px 0' }} />

                <div style={{ fontWeight: 950, color: '#111827' }}>Pricing</div>

                {effectiveQuote && !effectiveQuote.manualQuote ? (
                  <div style={{ display: 'grid', gap: 10 }}>
                    {(() => {
                      const total = Number(effectiveQuote.total || 0);
                      const addons = Number(effectiveQuote.addonsTotal || 0);
                      const delivery = Number(deliveryFeesUsd || 0);

                      const bd = normalizeBreakdown(effectiveQuote?.breakdown);
                      const siteDiscount = getSiteDiscountSummary(effectiveQuote?.breakdown);
                      const breakdownBase = Number(bd?.baseTotal);

                      const baseRental =
                        Number.isFinite(breakdownBase)
                          ? breakdownBase
                          : Math.max(0, Number(roundMoney(total - addons - delivery) ?? 0));
                      const baseRentalBeforeDiscount = Number.isFinite(Number(siteDiscount?.rentalBefore))
                        ? Number(siteDiscount.rentalBefore)
                        : baseRental;
                      const rentalDiscountAmount = Number.isFinite(Number(siteDiscount?.discountAmount))
                        ? Number(siteDiscount.discountAmount)
                        : Math.max(0, Number(roundMoney(baseRentalBeforeDiscount - baseRental) ?? 0));
                      const totalBeforeDiscount = Number.isFinite(Number(siteDiscount?.totalBefore))
                        ? Number(siteDiscount.totalBefore) + delivery
                        : total + rentalDiscountAmount;
                      const isAL = safeStr(countryCode).toUpperCase() === 'AL';
                      const full = safeStr(insuranceTier) === 'FULL';
                      const depositObj = policyDeposit; // {amount,currency} | null
                      const depositDue = depositObj?.amount;
                      const depositCur = depositObj?.currency;

                      const depositLine =
                        isAL && full
                          ? 'No deposit required (Full coverage selected in Albania).'
                          : (depositDue != null && depositCur)
                            ? `Deposit is taken on arrival: ${fmt(depositDue, depositCur)}.`
                            : `Deposit is taken on arrival.`;
                      return (
                        <>
                          <Notice tone="success" title={SITE_RESERVATION_DISCOUNT_LABEL}>
                            The 10% site discount is applied to the rental amount only. Insurance, extras, and delivery
                            charges are shown separately.
                          </Notice>

                          <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 900 }}>
                            Total price is built as:
                          </div>

                          <div style={priceRow}>
                            <div style={priceLeft}>Rental before site discount</div>
                            <div style={{ ...priceRight, textDecoration: 'line-through', opacity: 0.7 }}>
                              {fmt(baseRentalBeforeDiscount, 'USD')}
                            </div>
                          </div>

                          <div style={priceRow}>
                            <div style={priceLeft}>Site discount (10% rental only)</div>
                            <div style={{ ...priceRight, color: '#2f5f1f' }}>
                              -{fmt(rentalDiscountAmount, 'USD')}
                            </div>
                          </div>

                          <div style={priceRow}>
                            <div style={priceLeft}>Rental after site discount</div>
                            <div style={priceRight}>{fmt(baseRental, 'USD')}</div>
                          </div>

                          <div style={priceRow}>
                            <div style={priceLeft}>
                              Insurance & extras{' '}
                            </div>
                            <div style={priceRight}>{fmt(addons, 'USD')}</div>
                          </div>

                          <div style={priceRow}>
                            <div style={priceLeft}>Pickup/Dropoff delivery</div>
                            <div style={priceRight}>{fmt(delivery, 'USD')}</div>
                          </div>

                          <div style={priceRow}>
                            <div style={priceLeft}>Total before site discount</div>
                            <div style={{ ...priceRight, textDecoration: 'line-through', opacity: 0.7 }}>
                              {fmt(totalBeforeDiscount, 'USD')}
                            </div>
                          </div>

                          <div style={{ ...priceRow, borderColor: BRAND_GREEN_DARK }}>
                            <div style={{ ...priceLeft, color: '#163510', fontWeight: 950 }}>Total now</div>
                            <div style={{ ...priceRight, fontWeight: 950 }}>{fmt(total, 'USD')}</div>

                          </div>

                          <Notice tone="info" title="Deposit on arrival">
                            {depositLine}
                          </Notice>

                        </>
                      );
                    })()}
                  </div>
                ) : (
                  <Notice tone="info" title="Quote required">
                    Pricing for this vehicle is not configured yet. Submit your request and we’ll email you a quote. The
                    reservation is not confirmed until you confirm by email.
                    {policyDeposit?.amount != null ? (
                      <div style={{ marginTop: 8 }}>
                        Deposit policy: <b>{fmt(Number(policyDeposit.amount), policyDeposit.currency || 'USD')}</b>
                      </div>
                    ) : null}
                  </Notice>
                )}

                <Divider style={{ margin: '6px 0' }} />

                <div style={{ fontWeight: 950, color: '#111827' }}>Your details</div>
                <div style={reviewGrid}>
                  <div style={reviewItem}>
                    <div style={reviewLabel}>Name</div>
                    <div style={reviewValue}>{safeStr(fullName) || '—'}</div>
                  </div>
                  <div style={reviewItem}>
                    <div style={reviewLabel}>Email</div>
                    <div style={reviewValue}>{safeStr(email) || '—'}</div>
                  </div>
                  <div style={reviewItem}>
                    <div style={reviewLabel}>Phone</div>
                    <div style={reviewValue}>{safeStr(phonePrimary) || '—'}</div>
                  </div>
                  <div style={reviewItem}>
                    <div style={reviewLabel}>WhatsApp</div>
                    <div style={reviewValue}>{safeStr(phoneWhatsapp) || safeStr(phonePrimary) || '—'}</div>
                  </div>
                  <div style={reviewItem}>
                    <div style={reviewLabel}>Flight</div>
                    <div style={reviewValue}>{safeStr(flightNumber) || '—'}</div>
                  </div>
                  <div style={reviewItem}>
                    <div style={reviewLabel}>Message</div>
                    <div style={reviewValue}>{safeStr(custMessage) || '—'}</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="rf-card-elevate rf-section-card rf-terms-section" style={{ ...reviewCard, marginTop: 12 }}>
              <div style={{ display: 'grid', gap: 12 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ fontWeight: 950, color: '#111827' }}>{ONLINE_RESERVATION_TERMS_TITLE}</div>
                    <div style={{ marginTop: 4, fontSize: 12, color: '#6b7280' }}>
                      Last updated: {ONLINE_RESERVATION_TERMS_LAST_UPDATED}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setTermsExpanded(v => !v)}
                    aria-expanded={termsExpanded}
                    style={{
                      border: `1px solid ${BRAND_GREEN_SOFT_BORDER}`,
                      background: '#ffffff',
                      color: BRAND_GREEN_TEXT,
                      borderRadius: 8,
                      padding: '8px 12px',
                      fontSize: 12,
                      fontWeight: 900,
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {termsExpanded ? 'Hide terms' : 'Show terms'}
                  </button>
                </div>

                {termsExpanded ? (
                  <div
                    style={{
                      border: `1px solid ${BRAND_GREEN_SOFT_BORDER}`,
                      background: BRAND_GREEN_SOFT,
                      borderRadius: 8,
                      padding: 14,
                      display: 'grid',
                      gap: 12,
                      maxHeight: 320,
                      overflowY: 'auto',
                    }}
                  >
                    {ONLINE_RESERVATION_TERMS_SECTIONS.map(section => (
                      <div key={section.title} style={{ display: 'grid', gap: 6 }}>
                        <div style={{ fontWeight: 900, color: '#111827', fontSize: 13 }}>{section.title}</div>
                        <ol style={{ margin: 0, paddingLeft: 18, display: 'grid', gap: 6, color: '#374151', fontSize: 13 }}>
                          {section.items.map(item => (
                            <li key={item}>{item}</li>
                          ))}
                        </ol>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div
                    style={{
                      border: `1px solid ${BRAND_GREEN_SOFT_BORDER}`,
                      background: BRAND_GREEN_SOFT,
                      borderRadius: 8,
                      padding: 14,
                      fontSize: 13,
                      color: '#374151',
                      lineHeight: 1.5,
                    }}
                  >
                    Review the online reservation terms before accepting them. Expand this section to read the full terms.
                  </div>
                )}

                <label
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 10,
                    padding: 12,
                    borderRadius: 8,
                    border: `1px solid ${termsAccepted ? BRAND_GREEN_SOFT_BORDER : '#d1d5db'}`,
                    background: termsAccepted ? '#f0fdf4' : '#ffffff',
                    color: '#111827',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={e => setTermsAccepted(e.target.checked)}
                    style={{ marginTop: 2 }}
                  />
                  <span style={{ fontSize: 13, lineHeight: 1.5 }}>
                    {ONLINE_RESERVATION_TERMS_CHECKBOX_LABEL}
                  </span>
                </label>

                <div style={{ fontSize: 12, color: '#6b7280' }}>
                  You must accept these terms before sending the request or proceeding to payment.
                </div>
              </div>
            </div>
            {/* Anti-spam + hidden fields */}
            {turnstileSiteKey ? (
              <div className="rf-card-elevate rf-section-card rf-captcha-section" style={{ ...reviewCard, marginTop: 12 }}>
                <div style={{ fontWeight: 950, color: '#111827' }}>Anti-spam verification</div>

                {/* Honeypot (hidden) */}
                <div style={{ position: 'absolute', left: -9999, top: -9999, height: 0, overflow: 'hidden' }}>
                  <label>
                    Website
                    <input
                      value={hpField}
                      onChange={e => setHpField(e.target.value)}
                      tabIndex={-1}
                      autoComplete="off"
                    />
                  </label>

                  {/* “Hidden fields” pattern (kept in DOM for parity; real values sent in payload too) */}
                  <input type="hidden" value="landingpage" readOnly />
                  <input type="hidden" value={countryCode} readOnly />
                  <input type="hidden" value={String(formTs)} readOnly />
                </div>

                <div style={{ marginTop: 10 }}>
                  <div ref={turnstileElRef} />
                  {turnstileError ? (
                    <div style={{ marginTop: 10, ...alertBad }}>{turnstileError}</div>
                  ) : null}

                  {!turnstileToken ? (
                    <div style={{ marginTop: 8, fontSize: 12, color: '#6b7280', fontWeight: 700 }}>
                      Complete the captcha to submit.
                    </div>
                  ) : (
                    <div style={{ marginTop: 8, fontSize: 12, color: '#2f5f1f', fontWeight: 900 }}>
                      Captcha completed.
                    </div>
                  )}

                  <div style={{ marginTop: 10 }}>
                    <button type="button" onClick={resetTurnstile} className="rf-cta rf-cta-secondary" style={actionSecondaryStyle}>
                      Reset captcha
                    </button>
                  </div>
                </div>
              </div>
            ) : null}

            <div className="rf-action-row" style={{ ...actionRowStyle, marginTop: 12 }}>

              {!submitOk ? (
                <div className="rf-action-row" style={{ ...actionRowStyle, marginTop: 12 }}>
                  <button type="button" onClick={() => goToStep(4)} className="rf-cta rf-cta-secondary" style={actionSecondaryStyle}>
                    Back
                  </button>

                  {showManualSubmit ? (
                    <button
                      type="button"
                      onClick={() => submitReservationRequest({ silent: false })}
                      disabled={submitLoading || quoteLoading || !termsAccepted}
                      className="rf-cta rf-cta-primary"
                      style={{
                        ...actionPrimaryStyle,
                        opacity: submitLoading || quoteLoading || !termsAccepted ? 0.55 : 1,
                        cursor: submitLoading || quoteLoading || !termsAccepted ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {submitLoading ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                          <Loader2 size={16} className="spin" /> Sending…
                        </span>
                      ) : (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                          <Mail size={16} /> {isManualPhone ? 'Send request' : 'Request a quote'}
                        </span>
                      )}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={proceedToPayment}
                      disabled={
                        submitLoading ||
                        quoteLoading ||
                        redirectingPay ||
                        payStatus === 'creating' ||
                        payStatus === 'redirecting' ||
                        !termsAccepted
                      }
                      className="rf-cta rf-cta-primary"
                      style={{
                        ...actionPrimaryStyle,
                        opacity:
                          submitLoading ||
                          quoteLoading ||
                          redirectingPay ||
                          payStatus === 'creating' ||
                          payStatus === 'redirecting' ||
                          !termsAccepted
                            ? 0.55
                            : 1,
                        cursor:
                          submitLoading ||
                          quoteLoading ||
                          redirectingPay ||
                          payStatus === 'creating' ||
                          payStatus === 'redirecting' ||
                          !termsAccepted
                            ? 'not-allowed'
                            : 'pointer',
                      }}
                    >
                      {redirectingPay || payStatus === 'creating' || payStatus === 'redirecting' ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                          <Loader2 size={16} className="spin" /> Redirecting…
                        </span>
                      ) : (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                          <ArrowRight size={16} /> Proceed to payment ({payNowUsd ? fmt(payNowUsd, 'USD') : '—'})
                        </span>
                      )}
                    </button>
                  )}

                </div>

              ) : null}

            </div>

            {quoteErr && (
              <Notice tone="error" title="Pricing failed">
                {quoteErr}
              </Notice>
            )}
            {submitErr && (
              <Notice tone="error" title="Submission failed">
                {submitErr}
              </Notice>
            )}
            {submitOk && (
              <Notice tone="success" title="Request received">
                Reference: <b>{submitOk.id}</b>. This reservation is not confirmed until the team replies.
              </Notice>
            )}

            {redirectingPay ? (
              <Notice tone="info" title="Redirecting to payment">
                You are being redirected to the payment page now.
              </Notice>
            ) : null}


            {!turnstileSiteKey ? (
              <Notice tone="warn" title="Anti-spam not configured">
                Turnstile key missing (optional).
              </Notice>
            ) : null}
            {payErr && (
              <Notice tone="error" title="Payment failed">
                {payErr}
              </Notice>
            )}

            {payStatus === 'verifying' && (
              <Notice tone="info" title="Verifying payment">
                Please wait…
              </Notice>
            )}

            {payStatus === 'paid' && (
              <Notice tone="success" title="Payment confirmed">
                {payReceipt?.orderId ? <>Order: <b>{String(payReceipt.orderId)}</b></> : 'Payment verified.'}
              </Notice>
            )}

          </div>
        )}
          </main>

          <aside className="rf-flow-summary" aria-label="Reservation summary">
            <div className="rf-summary-card">
              <div className="rf-summary-header">
                <div>
                  <span className="rf-summary-kicker">Your reservation</span>
                  <h4>{summaryCarLabel}</h4>
                </div>
                {step > 1 ? (
                  <button type="button" onClick={() => goToStep(1)}>Edit trip</button>
                ) : null}
              </div>

              <div className="rf-summary-list">
                <div className="rf-summary-row">
                  <span className="rf-summary-icon"><MapPin size={16} /></span>
                  <div>
                    <small>Route</small>
                    <strong>{safeStr(country) || 'Choose a country'}</strong>
                    <span>{safeStr(pickupFinal) || 'Pick-up not selected'} → {safeStr(dropoffFinal) || 'Drop-off not selected'}</span>
                  </div>
                </div>
                <div className="rf-summary-row">
                  <span className="rf-summary-icon"><Clock3 size={16} /></span>
                  <div>
                    <small>Dates</small>
                    <strong>{rentalDays ? `${rentalDays} day rental` : 'Choose your dates'}</strong>
                    <span>{formatDateTimeNice(startLocal, tz)} → {formatDateTimeNice(endLocal, tz)}</span>
                  </div>
                </div>
                <div className="rf-summary-row">
                  <span className="rf-summary-icon"><CarFront size={16} /></span>
                  <div>
                    <small>Vehicle</small>
                    <strong>{selectedCar ? formatCarNameWithoutYearPlate(selectedCar, '') : 'Not selected yet'}</strong>
                    <span>{selectedCar ? `${safeStr(selectedCar.transmission) || 'Any transmission'} · ${safeStr(selectedCar.fuelType) || 'Any fuel'}` : 'Available cars appear after your search'}</span>
                  </div>
                </div>
                <div className="rf-summary-row">
                  <span className="rf-summary-icon"><ShieldCheck size={16} /></span>
                  <div>
                    <small>Protection</small>
                    <strong>{insuranceTier === 'FULL' ? 'Full coverage' : 'Third-party coverage'}</strong>
                    <span>{summaryExtrasCount ? `${summaryExtrasCount} extra ${summaryExtrasCount === 1 ? 'item' : 'items'}` : 'No extras added'}</span>
                  </div>
                </div>
              </div>

              <div className="rf-summary-price" aria-live="polite">
                <div>
                  <span>Estimated total</span>
                  <small>{selectedCar ? `${rentalDays || '—'} days · taxes and fees included` : 'Updates after vehicle selection'}</small>
                </div>
                <strong>{quoteLoading ? 'Calculating…' : hasSummaryQuote ? fmt(summaryQuoteTotal, 'USD') : '—'}</strong>
              </div>

              {payNowUsd ? (
                <div className="rf-summary-pay-now"><CreditCard size={15} /> Pay now: <strong>{fmt(payNowUsd, 'USD')}</strong></div>
              ) : null}
              {policyDeposit?.amount != null ? (
                <div className="rf-summary-deposit">Deposit on arrival: <strong>{fmt(Number(policyDeposit.amount), policyDeposit.currency || 'USD')}</strong></div>
              ) : null}

              <div className="rf-summary-assurance">
                <LockKeyhole size={16} />
                <div><strong>Secure reservation</strong><span>Your trip is not confirmed until approval or verified payment.</span></div>
              </div>
              <div className="rf-summary-benefit"><CheckCircle2 size={15} /> 10% online discount on the rental rate</div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
